import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Fab, Paper, InputBase, IconButton, List, ListItem, ListItemButton, ListItemText, CircularProgress, Button, TextField, Typography, Popper, Tooltip } from '@mui/material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import DirectionsIcon from '@mui/icons-material/Directions';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Map as MapLibreMap, Marker, Source, Layer } from '@vis.gl/react-maplibre';
import type { MapRef } from '@vis.gl/react-maplibre';
import type { FeatureCollection, LineString } from 'geojson';
import type { QuikQStation } from '../services/quikqApi';
import { fuelPricesApiService } from '../services/fuelPricesApi';
import { socketService } from '../services/socketService';
import { getStationIcon } from '../utils/stationIcons';
import {
  getRepricingDisplayPrice,
  getDisplayPriceWithOverride,
  shouldShowRepricingRow,
  getFuelNetworkDisplayName,
  getStationCardPrices,
  isDualPriceNetworkStation,
  filterStationSearchResults,
  scoreStationSearchMatch,
} from '../utils/fuelTypes';
import { dedupeStationsByKey } from '../utils/stationDedupe';
import { logger, LogCategory } from '../services/logger';
import { quikqAuthService } from '../services/quikqAuth';
import { getActiveOverrides, clearOverridesCache } from '../services/priceOverrideService';
import RouteCalculationOverlay from '../components/RouteCalculationOverlay';
import {
  buildRouteCacheKey,
  getCachedRouteStations,
  setCachedRouteStations,
  clearStationsCache,
} from '../services/stationsCache';
import 'maplibre-gl/dist/maplibre-gl.css';

/**
 * Map + route planner UI. Caching for stations/routes lives in `stationsCache.ts`
 * and is consumed via `fuelPricesApi.ts` — keep new map UX changes in separate PRs
 * from cache/auth work when possible (see stationsCache.ts maintainer note).
 */

const containerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  position: 'relative',
  zIndex: 1,
  margin: 0,
  padding: 0
};

const defaultCenter = { lat: 41.8781, lng: -87.6298 }; // Chicago
const USE_TEST_LOCATION = false; // production: use real device location
const TEST_LOCATION = defaultCenter;

// OpenFreeMap Positron — same light grayscale look as CARTO, no API key.
const MAP_STYLE = 'https://tiles.openfreemap.org/styles/positron';

// Z-index hierarchy: route start/end stay below NSF & station popups so pins/labels don't block "Add Stop"
const Z = {
  NON_FUEL: 2600,
  NSF: 2700,
  // Route start/destination pins sit just above the station markers so the pin
  // glyph itself is always visible, but their popups stay below the station
  // detail popup (with "Add as Stop") so that one always wins when both are
  // visible in the same area.
  START_ROUTE: 2720,
  DESTINATION: 2730,
  SEARCH_TARGET: 2750,
  CHECKPOINT: 2800,
  ROUTE_ENDPOINT_POPUP: 2850,
  CHECKPOINT_POPUP: 2900,
  STATION_POPUP_ADD_STOP: 3000,
} as const;

type GeoPermissionState = 'granted' | 'denied' | 'prompt' | 'unknown';

type RouteOption = {
  latlngs: Array<{ lat: number; lng: number }>;
  distanceMi: number;
  durationMin: number;
};

const stationDedupeScore = (s: QuikQStation): number =>
  Array.isArray(s.repricing) && s.repricing.length > 0 ? 1 : 0;

/**
 * Compact popup attached to the route start/destination pins.
 * Sized to fit on small phone viewports and dismissable so it never permanently
 * covers nearby station markers.
 */
const RouteEndpointPopup: React.FC<{
  title: string;
  label: string;
  accentColor: string;
  onClose: () => void;
}> = ({ title, label, accentColor, onClose }) => {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: 10,
        padding: '6px 8px 6px 10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
        marginBottom: 6,
        width: 'max-content',
        maxWidth: 'min(78vw, 240px)',
        minWidth: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 6,
        borderLeft: `3px solid ${accentColor}`,
        animation: 'nsf-popup-fade-in 0.18s ease-out',
        pointerEvents: 'auto',
      }}
    >
      <div style={{ minWidth: 0, flex: 1 }}>
        <div
          style={{
            fontWeight: 700,
            fontSize: 11,
            color: accentColor,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            lineHeight: 1.1,
            marginBottom: 2,
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: '#222',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
          }}
          title={label}
        >
          {label}
        </div>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        aria-label={`Hide ${title} details`}
        style={{
          flex: '0 0 auto',
          width: 20,
          height: 20,
          padding: 0,
          border: 'none',
          background: '#f1f3f5',
          color: '#555',
          borderRadius: '50%',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
};

const MapRoutesMapLibre: React.FC = () => {
  const mapRef = useRef<MapRef | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const didRequestInitialLocationRef = useRef(false);
  const [center, setCenter] = useState<{ lat: number; lng: number }>(() => {
    if (USE_TEST_LOCATION) return TEST_LOCATION;
    try {
      const saved = localStorage.getItem('nsf_lastLocation');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
          return { lat: parsed.lat, lng: parsed.lng };
        }
      }
    } catch {
      // ignore
    }
    return defaultCenter;
  });
  const [myLocation, setMyLocation] = useState<{ lat: number; lng: number } | null>(() => {
    if (USE_TEST_LOCATION) return TEST_LOCATION;
    try {
      const saved = localStorage.getItem('nsf_lastLocation');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') {
          return { lat: parsed.lat, lng: parsed.lng };
        }
      }
    } catch {
      // ignore
    }
    return null;
  });
  const [stations, setStations] = useState<QuikQStation[]>([]);
  // Visible bounds of the map viewport. Used to render only the markers that
  // are actually on screen (plus a small padding), which keeps the DOM small
  // even when the in-memory station list contains thousands of entries.
  // `null` means "render everything" (e.g. before first moveend fires).
  const [viewportBounds, setViewportBounds] = useState<{
    south: number;
    west: number;
    north: number;
    east: number;
  } | null>(null);
  const [loadingStations, setLoadingStations] = useState(false);
  const [stationsLoadReason, setStationsLoadReason] = useState<'init' | 'moveend' | 'locate' | 'search' | 'route' | null>(null);
  // True from the moment a new route is created until its station fetch
  // completes. Bridges the small async gap between routeLoading turning false
  // and fetchStations actually flipping loadingStations on, so the calculation
  // overlay never disappears prematurely (even if a competing fetch is in flight).
  const [pendingRouteStations, setPendingRouteStations] = useState(false);
  // Mirror of pendingRouteStations in a ref, so scheduleStationFetch (and any
  // other non-React-render code path) can synchronously check the current
  // value without having to re-create the callback on every state change.
  const pendingRouteStationsRef = useRef(false);
  React.useEffect(() => {
    pendingRouteStationsRef.current = pendingRouteStations;
  }, [pendingRouteStations]);
  const routeFetchObservedRef = useRef(false);
  const loadingRef = useRef(false);
  const lastZoomRef = useRef<number | null>(null);
  const isLocatingRef = useRef(false); // Flag to track if we're currently locating
  const [centerForSuggestions, setCenterForSuggestions] = useState<{ lat: number; lng: number } | null>(null);
  const centerForSuggestionsRef = useRef<{ lat: number; lng: number } | null>(null);
  const [searchTarget, setSearchTarget] = useState<{ lat: number; lng: number; label?: string } | null>(null);
  const [routeLatLngs, setRouteLatLngs] = useState<Array<{ lat: number; lng: number }>>(() => {
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.routeLatLngs && Array.isArray(parsed.routeLatLngs) && parsed.routeLatLngs.length > 0) {
          return parsed.routeLatLngs;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [routeSummary, setRouteSummary] = useState<{ distanceMi: number; durationMin: number } | null>(() => {
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.routeSummary) {
          return parsed.routeSummary;
        }
      }
    } catch {
      // ignore
    }
    return null;
  });
  const [altRoutes, setAltRoutes] = useState<RouteOption[]>(() => {
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.altRoutes && Array.isArray(parsed.altRoutes)) {
          return parsed.altRoutes;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [selectedRouteIndex, setSelectedRouteIndex] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed?.selectedRouteIndex === 'number') {
          return parsed.selectedRouteIndex;
        }
      }
    } catch {
      // ignore
    }
    return -1; // -1 means no route selected
  });
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState<string | null>(null);
  const lastRouteCreatedLogKeyRef = useRef<string | null>(null);
  const lastRouteStationsLogKeyRef = useRef<string | null>(null);
  const didMountRouteLogRef = useRef(false);
  const [routeDestination, setRouteDestination] = useState<{ lat: number; lng: number; label?: string } | null>(() => {
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.routeDestination) {
          return parsed.routeDestination;
        }
      }
    } catch {
      // ignore
    }
    return null;
  });
  const [suggestedCollapsed, setSuggestedCollapsed] = useState(true);
  const [isSearchBarHidden, setIsSearchBarHidden] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [isRequestingInitialLocation, setIsRequestingInitialLocation] = useState(false);
  const [locationPromptMessage, setLocationPromptMessage] = useState<string | null>(null);
  const [geoPermission, setGeoPermission] = useState<GeoPermissionState>('unknown');
  const [socketConnected, setSocketConnected] = useState(false);
  const [popupRefreshKey, setPopupRefreshKey] = useState(0);
  const [priceOverridesMap, setPriceOverridesMap] = useState<Record<string, any>>({});
  const loadPriceOverrides = useCallback(async () => {
    try {
      const overrides = await getActiveOverrides();
      setPriceOverridesMap(overrides || {});
    } catch (error) {
      console.warn('Failed to load price overrides:', error);
    }
  }, []);

  React.useEffect(() => {
    loadPriceOverrides();
    const refreshId = window.setInterval(loadPriceOverrides, 60_000);
    return () => window.clearInterval(refreshId);
  }, [loadPriceOverrides]);

  const hideSearchBar = useCallback(() => setIsSearchBarHidden(true), []);
  const showSearchBar = useCallback(() => setIsSearchBarHidden(false), []);

  // -------------------------------------------------------------------------
  // FAB stack positioning — track the REAL height of the "Suggested nearby"
  // panel via ResizeObserver, then pin the floating controls just above it.
  //
  // We used to hardcode heights (~90 collapsed, ~290 expanded), but that
  // broke in two cases:
  //   1) Collapsed on narrow screens: the disclaimer wraps to 3-4 lines, so
  //      the real height grows past ~90 and the bottom FAB (location) ended
  //      up hidden behind the panel.
  //   2) Expanded but EMPTY ("Loading stations..." / "No stations found"):
  //      the panel is much shorter than the hardcoded ~290, so there was a
  //      huge empty gap between the FAB stack and the panel.
  //
  // Measuring the actual rendered height makes both cases self-correcting.
  // -------------------------------------------------------------------------
  const suggestedPanelRef = useRef<HTMLDivElement>(null);
  const [suggestedPanelHeight, setSuggestedPanelHeight] = useState(0);

  React.useEffect(() => {
    const el = suggestedPanelRef.current;
    if (!el) return;
    setSuggestedPanelHeight(el.getBoundingClientRect().height);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSuggestedPanelHeight(entry.contentRect.height);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Bottom-edge offset of the suggested panel from the viewport bottom (it
  // sits above the bottom nav). Keep these numbers in sync with the panel's
  // `bottom` prop further down in the JSX.
  // Math: BottomNav bar is 64 px tall, the active "donut" pokes 21 px above
  // the bar (= 85 px peak from viewport bottom). The panel bottom MUST be
  // greater than that peak, otherwise the donut visually punches into the
  // panel (e.g. overlapping the disclaimer text). We add ~5-9 px of
  // breathing room above the donut peak for a clean separation.
  const SUGGESTED_PANEL_OFFSET = { xs: 90, sm: 92, md: 94 } as const;
  // Visual gap between the FAB stack / route picker and the panel.
  const FAB_PANEL_GAP = 12;

  const controlsBottom = useMemo(
    () => ({
      xs: SUGGESTED_PANEL_OFFSET.xs + suggestedPanelHeight + FAB_PANEL_GAP,
      sm: SUGGESTED_PANEL_OFFSET.sm + suggestedPanelHeight + FAB_PANEL_GAP,
      md: SUGGESTED_PANEL_OFFSET.md + suggestedPanelHeight + FAB_PANEL_GAP,
    }),
    [suggestedPanelHeight]
  );

  const routeSelectionBottom = controlsBottom;
  const [openStationCode, setOpenStationCode] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<
    Array<{ label: string; lat: number; lon: number; kind?: 'place' | 'station' }>
  >([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const fetchSuggestionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchStationsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const scheduleStationsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const activeStationsAbortRef = useRef<AbortController | null>(null);
  const lastStationFetchKeyRef = useRef<string | null>(null);
  const lastStationFetchAtRef = useRef<number>(0);
  const stationDetailsCacheRef = useRef<Map<string, { ts: number; repricing: QuikQStation['repricing'] }>>(new Map());
  const stationDetailsInflightRef = useRef<Map<string, Promise<void>>>(new Map());
  const [isPlanRouteOpen, setIsPlanRouteOpen] = useState(false);
  const [plannerSuggestions, setPlannerSuggestions] = useState<Array<{ label: string; lat: number; lon: number }>>([]);
  const [plannerActiveField, setPlannerActiveField] = useState<{ type: 'start' | 'destination' | 'checkpoint'; id?: string } | null>(null);
  const [plannerAnchorEl, setPlannerAnchorEl] = useState<HTMLElement | null>(null);
  const [isPlannerSubmitting, setIsPlannerSubmitting] = useState(false);
  const [activeCheckpointPopupId, setActiveCheckpointPopupId] = useState<string | null>(null);
  const [showRouteStartPopup, setShowRouteStartPopup] = useState(true);
  const [showRouteDestinationPopup, setShowRouteDestinationPopup] = useState(true);
  const plannerSuggestionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedSocketRef = useRef(false);
  const hasMountedSocketCleanupRef = useRef(false);
  const [routePlanner, setRoutePlanner] = useState<{
    start: { label: string; coords: { lat: number; lng: number } | null };
    destination: { label: string; coords: { lat: number; lng: number } | null };
    checkpoints: Array<{ id: string; label: string; coords: { lat: number; lng: number } | null }>;
  }>(() => {
    // Try to restore from localStorage
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.routePlanner) {
          return parsed.routePlanner;
        }
      }
    } catch {
      // ignore
    }
    return {
      start: { label: '', coords: null },
      destination: { label: '', coords: null },
      checkpoints: [],
    };
  });

  // Save route state to localStorage
  const saveRouteState = useCallback(() => {
    try {
      const routeState = {
        routeLatLngs,
        routeSummary,
        routeDestination,
        routePlanner,
        altRoutes,
        selectedRouteIndex,
      };
      localStorage.setItem('nsf_lastRoute', JSON.stringify(routeState));
    } catch (error) {
      // Ignore localStorage errors
      console.warn('Failed to save route state:', error);
    }
  }, [routeLatLngs, routeSummary, routeDestination, routePlanner, altRoutes, selectedRouteIndex]);

  const distanceMiles = useCallback((a: { lat: number; lng: number }, b: { lat: number; lng: number }): number => {
    const R = 6371000; // meters
    const toRad = (x: number) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const sinDLat = Math.sin(dLat / 2);
    const sinDLon = Math.sin(dLon / 2);
    const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
    const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
    return (R * c) / 1609.34;
  }, []);

  const computeRadiusMiles = useCallback((): number => {
    const map = mapRef.current;
    if (!map) return 75;
    try {
      const bounds = map.getBounds();
      const centerLatLng = bounds.getCenter();
      const center = { lat: centerLatLng.lat, lng: centerLatLng.lng };
      const corners = [
        bounds.getNorthEast(),
        bounds.getNorthWest(),
        bounds.getSouthEast(),
        bounds.getSouthWest(),
      ];
      const miles = Math.max(
        ...corners.map((c) => distanceMiles(center, { lat: c.lat, lng: c.lng }))
      );
      return Math.max(5, Math.min(220, Math.round(miles)));
    } catch {
      return 75;
    }
  }, [distanceMiles]);

  const fitBoundsToRoute = useCallback(
    (latlngs: Array<{ lat: number; lng: number }>) => {
      if (!mapRef.current || !isMapReady || latlngs.length < 2) return;
      try {
        let minLat = latlngs[0].lat;
        let maxLat = latlngs[0].lat;
        let minLng = latlngs[0].lng;
        let maxLng = latlngs[0].lng;

        for (const p of latlngs) {
          if (p.lat < minLat) minLat = p.lat;
          if (p.lat > maxLat) maxLat = p.lat;
          if (p.lng < minLng) minLng = p.lng;
          if (p.lng > maxLng) maxLng = p.lng;
        }

        mapRef.current.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          { padding: 40 }
        );
      } catch (error) {
        console.error('Error fitting bounds:', error);
      }
    },
    [isMapReady]
  );

  const fetchStations = useCallback(async (options?: { signal?: AbortSignal; reason?: 'init' | 'moveend' | 'locate' | 'search' | 'route' }) => {
    // For non-route reasons, defer to the in-flight request to avoid duplicate
    // work. For route fetches we must always proceed (callers depend on the
    // resulting state transition to close the calculation overlay), so we wait
    // for the current request to release loadingRef instead of bailing out.
    if (loadingRef.current) {
      if (options?.reason !== 'route') return;
      // Wait briefly for the in-flight request to finish (it has been aborted
      // by scheduleStationFetch). Cap the wait so we don't stall forever.
      const waitStart = Date.now();
      while (loadingRef.current && Date.now() - waitStart < 3000) {
        await new Promise<void>((resolve) => setTimeout(resolve, 30));
      }
      if (loadingRef.current) return;
    }
    const map = mapRef.current;
    if (!map) return;
    loadingRef.current = true;
    setLoadingStations(true);
    setStationsLoadReason(options?.reason ?? null);
    if (options?.reason === 'route') {
      routeFetchObservedRef.current = true;
    }
    // Tracks whether this fetch reached a real terminal state (success or
    // non-abort error). We only close the calculation overlay when this is
    // true — aborted fetches must NOT close it, because another fetch will
    // take over and we don't want the animation to disappear in the gap.
    let routeFetchSettled = false;
    try {
      let stationsResp: QuikQStation[] = [];
      
      // If route exists, fetch stations along the entire route
      if (routeLatLngs.length > 0) {
        // Route-level cache: if the exact same start/end/checkpoints combo has
        // been fetched recently (and is still fresh per the Chicago 9 AM /
        // 5 PM schedule), short-circuit the whole sample-point fan-out and
        // return the cached station list immediately.
        const routeCacheKey = buildRouteCacheKey({
              startCode: 'fuel_prices',
              start: routeLatLngs[0],
              end: routeLatLngs[routeLatLngs.length - 1],
              checkpoints: routePlanner.checkpoints
                .map((cp) => cp.coords)
                .filter((c): c is { lat: number; lng: number } => !!c),
            });

        if (options?.reason === 'route') {
          try {
            const cachedRoute = await getCachedRouteStations(routeCacheKey);
            if (cachedRoute && cachedRoute.data.length > 0) {
              const deduped = dedupeStationsByKey(cachedRoute.data, stationDedupeScore);
              if (!options?.signal?.aborted) {
                setStations(deduped);
              }
              // Mark the fetch as settled BEFORE returning / falling through
              // so the finally block actually closes the calculation overlay.
              // Without this, a fresh cache hit would keep the overlay open
              // until the 45s safety timeout, making it look slower than a
              // real network fetch.
              routeFetchSettled = true;
              if (cachedRoute.isFresh) {
                return;
              }
              // Stale: we rendered immediately from cache; the network refresh
              // below will silently update the map and the route cache when
              // it finishes.
            }
          } catch (error) {
            // Cache read failures must never block the network path.
            console.warn('stationsCache(route): lookup failed, falling through', error);
          }
        }

        // Strategy: Fetch stations at multiple points along the route to cover entire length.
        // The downstream filter only keeps stations within ~3 miles of the route line,
        // so there's no value in pulling huge radii around each sample — that just
        // bloats memory with stations we'll throw away.
        // Use a tighter sample interval with a smaller radius so we still cover
        // the entire route with overlap between samples.
        const SAMPLE_INTERVAL_MILES = 70;
        const SEARCH_RADIUS_MILES = 40; // ~10mi overlap between adjacent samples
        
        // Calculate cumulative distances along route
        const routePoints: Array<{ point: { lat: number; lng: number }; cumulativeDist: number }> = [];
        let cumulativeDist = 0;
        
        routePoints.push({ point: routeLatLngs[0], cumulativeDist: 0 });
        
        for (let i = 1; i < routeLatLngs.length; i++) {
          const segmentDist = distanceMiles(routeLatLngs[i - 1], routeLatLngs[i]);
          cumulativeDist += segmentDist;
          routePoints.push({ point: routeLatLngs[i], cumulativeDist });
        }
        
        // Sample points along route at regular intervals
        const samplePoints: Array<{ lat: number; lng: number }> = [];
        const totalRouteDist = cumulativeDist;
        
        // Always include start and end points
        samplePoints.push(routeLatLngs[0]);
        samplePoints.push(routeLatLngs[routeLatLngs.length - 1]);
        
        // Add intermediate sample points
        for (let dist = SAMPLE_INTERVAL_MILES; dist < totalRouteDist; dist += SAMPLE_INTERVAL_MILES) {
          // Find the two route points that bracket this distance
          for (let j = 0; j < routePoints.length - 1; j++) {
            const p1 = routePoints[j];
            const p2 = routePoints[j + 1];
            
            if (dist >= p1.cumulativeDist && dist <= p2.cumulativeDist) {
              // Interpolate between p1 and p2
              const t = (dist - p1.cumulativeDist) / (p2.cumulativeDist - p1.cumulativeDist);
              const interpolatedLat = p1.point.lat + t * (p2.point.lat - p1.point.lat);
              const interpolatedLng = p1.point.lng + t * (p2.point.lng - p1.point.lng);
              samplePoints.push({ lat: interpolatedLat, lng: interpolatedLng });
              break;
            }
          }
        }
        
        // Fetch stations for each sample point
        const allStations = new Map<string, QuikQStation>(); // Use Map to deduplicate by stationCode
        
        // Fetch stations in parallel (but limit concurrent requests)
        const BATCH_SIZE = 5;
        for (let i = 0; i < samplePoints.length; i += BATCH_SIZE) {
          const batch = samplePoints.slice(i, i + BATCH_SIZE);
          const batchPromises = batch.map(point => 
            fuelPricesApiService.getStations({
              latitude: point.lat, 
              longitude: point.lng, 
              radius: SEARCH_RADIUS_MILES 
            }, { signal: options?.signal }).catch(() => []) // Ignore errors for individual requests
          );
          
          const batchResults = await Promise.all(batchPromises);
          batchResults.forEach(stations => {
            if (Array.isArray(stations)) {
              stations.forEach(station => {
                if (station.stationCode) {
                  allStations.set(station.stationCode, station);
                }
              });
            }
          });
        }
        
        stationsResp = Array.from(allStations.values());

        // Persist the full station list for this route hash so the next time
        // the user creates the *same* route it loads instantly from IndexedDB
        // instead of fanning out network calls again.
        if (routeCacheKey && stationsResp.length > 0 && !options?.signal?.aborted) {
          void setCachedRouteStations(routeCacheKey, stationsResp, 'fuel_prices');
        }

        // Log which stations were loaded for this route (deduped, truncated to keep log size reasonable)
        try {
          const start = routeLatLngs[0];
          const end = routeLatLngs[routeLatLngs.length - 1];
          const routeKey = `${start.lat.toFixed(4)},${start.lng.toFixed(4)}->${end.lat.toFixed(4)},${end.lng.toFixed(4)}|pts:${routeLatLngs.length}`;
          if (lastRouteStationsLogKeyRef.current !== routeKey) {
            lastRouteStationsLogKeyRef.current = routeKey;
            const MAX_STATIONS_IN_LOG = 150;
            const stationsInLog = stationsResp.slice(0, MAX_STATIONS_IN_LOG).map(st => ({
              stationCode: st.stationCode,
              stationName: st.stationName,
              city: st.city,
              state: st.state
            }));
            logger.logUserAction('Map: Stations loaded for route', {
              routeKey,
              samplePointsCount: samplePoints.length,
              sampleIntervalMiles: SAMPLE_INTERVAL_MILES,
              searchRadiusMiles: SEARCH_RADIUS_MILES,
              stationCount: stationsResp.length,
              stationsTruncated: stationsResp.length > MAX_STATIONS_IN_LOG,
              stations: stationsInLog
            });
          }
        } catch (logError) {
          // Never break map flow due to logging
          console.warn('Failed to log route stations:', logError);
        }
      } else {
        // No route - use map center as before
        const c = map.getCenter();
        const radius = computeRadiusMiles();
        const centerStations = await fuelPricesApiService.getStations(
          { latitude: c.lat, longitude: c.lng, radius },
          { signal: options?.signal }
        );
        stationsResp = centerStations || [];
      }

      stationsResp = dedupeStationsByKey(stationsResp || [], stationDedupeScore);

      if (options?.signal?.aborted) return;
      setStations(stationsResp);
      // Mark the fetch as settled on a real success path. Aborted fetches
      // must NOT mark this true — another fetch will take over and we don't
      // want the overlay to disappear in the gap. Any successful route-aware
      // fetch (regardless of `reason`) is enough to close the overlay,
      // because the stations along the route are now populated.
      routeFetchSettled = true;
    } catch (e: any) {
      if (e?.name === 'AbortError' || options?.signal?.aborted) {
        // Aborted: another fetch is taking over (e.g. user panned the map
        // mid-route-load). Don't close the overlay here.
        return;
      }
      console.error('fuel_prices station fetch failed:', e?.message || e);
      setStations([]);
      // Non-abort error: the fetch is truly done, so let the overlay close so
      // the user isn't stuck on the animation forever.
      routeFetchSettled = true;
      // Auto-logout if auth error
      if (e?.message?.includes('authentication token') || e?.message?.includes('expired') || e?.message?.includes('Session expired')) {
        console.warn('🔐 Auth token issue detected. Redirecting to login...');
        window.location.href = '/login';
      }
    } finally {
      setLoadingStations(false);
      setStationsLoadReason(null);
      loadingRef.current = false;
      // Close the route-calculation overlay if a route is active and this
      // fetch reached a terminal state (success or non-abort error). We do
      // this regardless of `reason` so that whichever route-aware fetch
      // happens to win the race (route useEffect, moveend, etc.) properly
      // hides the animation once the stations along the route are loaded.
      // Aborted fetches deliberately do NOT close the overlay.
      const routeIsActive = routeLatLngs && routeLatLngs.length >= 2;
      if (routeIsActive && routeFetchSettled) {
        // Defer closing until the next two paint frames so MapLibre has time
        // to actually render the new station markers to the canvas. Otherwise
        // the overlay disappears in the same React commit as setStations(),
        // but the markers only paint one frame later, so the user briefly
        // sees an empty route before stations pop in.
        const closeOverlay = () => {
          pendingRouteStationsRef.current = false;
          setPendingRouteStations(false);
          routeFetchObservedRef.current = false;
        };
        if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(closeOverlay);
          });
        } else {
          closeOverlay();
        }
      }
    }
  }, [computeRadiusMiles, routeLatLngs, distanceMiles]);

  const loadStationDetailsOnDemand = useCallback(async (station: QuikQStation) => {
    if (!station?.stationCode) return;
    if (Array.isArray(station.repricing) && station.repricing.length > 0) return;

    const stationCode = station.stationCode;
    const now = Date.now();
    const DETAILS_TTL_MS = 5 * 60 * 1000;
    const cached = stationDetailsCacheRef.current.get(stationCode);
    if (cached && now - cached.ts < DETAILS_TTL_MS && Array.isArray(cached.repricing) && cached.repricing.length > 0) {
      setStations(prev =>
        prev.map(s => (s.stationCode === stationCode ? { ...s, repricing: cached.repricing } : s))
      );
      return;
    }

    const inflight = stationDetailsInflightRef.current.get(stationCode);
    if (inflight) {
      await inflight;
      return;
    }

    const detailPromise = (async () => {
      try {
        const detailedStations = await fuelPricesApiService.getStations({
          latitude: station.latitude,
          longitude: station.longitude,
          radius: 1,
          stationCode
        });
        const detailedStation = detailedStations?.find(s => s.stationCode === stationCode);
        if (detailedStation && Array.isArray(detailedStation.repricing) && detailedStation.repricing.length > 0) {
          stationDetailsCacheRef.current.set(stationCode, { ts: Date.now(), repricing: detailedStation.repricing });
          setStations(prev =>
            prev.map(s => (s.stationCode === stationCode ? { ...s, repricing: detailedStation.repricing } : s))
          );
        }
      } catch {
        // On-demand detail backfill is best-effort only.
      } finally {
        stationDetailsInflightRef.current.delete(stationCode);
      }
    })();

    stationDetailsInflightRef.current.set(stationCode, detailPromise);
    await detailPromise;
  }, []);

  const scheduleStationFetch = useCallback((reason: 'init' | 'moveend' | 'locate' | 'search' | 'route', delayMs?: number) => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;

    // While the route-calculation animation is still active (route fetch in
    // flight), suppress non-route fetches. The in-flight route fetch already
    // pulls stations along the entire route — letting a moveend (triggered
    // by fitBoundsToRoute's pan animation) abort it would leave the overlay
    // stuck open and stations missing.
    if (reason !== 'route' && pendingRouteStationsRef.current) {
      return;
    }

    const center = map.getCenter();
    const radius = computeRadiusMiles();
    const routeKey = routeLatLngs.length > 0 ? `route:${routeLatLngs.length}` : 'route:none';
    const requestKey = `${center.lat.toFixed(2)}|${center.lng.toFixed(2)}|${radius}|${routeKey}`;
    const now = Date.now();
    if (requestKey === lastStationFetchKeyRef.current && now - lastStationFetchAtRef.current < 900) {
      return;
    }

    if (scheduleStationsTimeoutRef.current) {
      clearTimeout(scheduleStationsTimeoutRef.current);
    }

    const defaultDelay = reason === 'moveend' ? 300 : 150;
    scheduleStationsTimeoutRef.current = setTimeout(() => {
      // Re-check the suppression guard at fire time — fitBounds' moveend may
      // have been queued before pendingRouteStations flipped to true.
      if (reason !== 'route' && pendingRouteStationsRef.current) {
        return;
      }
      lastStationFetchKeyRef.current = requestKey;
      lastStationFetchAtRef.current = Date.now();
      if (activeStationsAbortRef.current) {
        activeStationsAbortRef.current.abort();
      }
      const controller = new AbortController();
      activeStationsAbortRef.current = controller;
      fetchStations({ signal: controller.signal, reason }).catch((e) => {
        if (e?.name !== 'AbortError') {
          console.warn('Station fetch failed:', e);
        }
      });
    }, delayMs ?? defaultDelay);
  }, [computeRadiusMiles, fetchStations, isMapReady, routeLatLngs.length]);

  const startDirectionsTo = useCallback(async (dest: { lat: number; lng: number }) => {
    const currentUser = quikqAuthService.getCurrentUser();
    logger.logUserAction('Map: Get Directions initiated', {
      destination: { lat: dest.lat, lng: dest.lng },
      userId: currentUser?.userId
    });
    
    hideSearchBar();
    setRouteLoading(true);
    setRouteError(null);
    try {
      // Ensure we have origin
      let origin = myLocation || center;
      if (!myLocation) {
        // Try to get precise location if possible, but don't wait too long
        if (navigator.geolocation) {
          await new Promise<void>((resolve) => {
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
                setMyLocation(origin);
                resolve();
              },
              () => resolve(), // Don't wait on error
              { enableHighAccuracy: false, maximumAge: 30000, timeout: 3000 } // Faster timeout
            );
          });
        }
      }

      const endpoints = [
        'https://router.project-osrm.org',
        'https://routing.openstreetmap.de/routed-car'
      ];
      let parsedRoutes: RouteOption[] | null = null;
      let lastError: Error | null = null;

      for (const base of endpoints) {
        try {
          const url = `${base}/route/v1/driving/${origin.lng},${origin.lat};${dest.lng},${dest.lat}?overview=full&alternatives=2&geometries=geojson`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout per attempt

          const res = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);

          if (!res.ok) throw new Error(`Route service error: ${res.status}`);
          const data = await res.json();
          const routes = Array.isArray(data?.routes) ? data.routes : [];
          if (!routes.length) {
            lastError = new Error('No routes returned');
            continue;
          }

          parsedRoutes = routes
            .map((r: any): RouteOption => {
              const coords: Array<[number, number]> = r.geometry.coordinates || [];
              const latlngs = coords.map(([lng, lat]) => ({ lat, lng }));
              return {
                latlngs,
                distanceMi: r.distance / 1609.34,
                durationMin: r.duration / 60
              };
            })
            .sort((a: RouteOption, b: RouteOption) => a.distanceMi - b.distanceMi);

          break;
        } catch (err: any) {
          lastError = err instanceof Error ? err : new Error('Unknown routing error');
        }
      }

      if (!parsedRoutes || parsedRoutes.length === 0) {
        throw lastError || new Error('Unable to calculate route');
      }

      setAltRoutes(parsedRoutes);
      const shortestRoute = parsedRoutes[0];
      setSelectedRouteIndex(0);
      setRouteLatLngs(shortestRoute.latlngs);
      setRouteSummary({ distanceMi: shortestRoute.distanceMi, durationMin: shortestRoute.durationMin });
      // Set destination marker
      const destLabel = searchTarget?.label || (stations.find(s => s.latitude === dest.lat && s.longitude === dest.lng)?.stationName) || 'Destination';
      setRouteDestination({ lat: dest.lat, lng: dest.lng, label: destLabel });
      setShowRouteStartPopup(true);
      setShowRouteDestinationPopup(true);
      routeFetchObservedRef.current = false;
      pendingRouteStationsRef.current = true;
      setPendingRouteStations(true);
      
      // Log successful route calculation
      const currentUser = quikqAuthService.getCurrentUser();
      logger.logUserAction('Map: Route calculated successfully', {
        destination: destLabel,
        distance: shortestRoute.distanceMi.toFixed(2),
        duration: shortestRoute.durationMin.toFixed(0),
        alternativeRoutes: parsedRoutes.length,
        userId: currentUser?.userId
      });
    } catch (error) {
      console.error('Route calculation error:', error);
      setRouteLatLngs([]);
      setRouteSummary(null);
      setAltRoutes([]);
      setSelectedRouteIndex(-1);
      setRouteDestination(null);
      setRouteError('Unable to calculate route right now. Please try again or zoom closer to the destination.');
    } finally {
      setRouteLoading(false);
    }
  }, [center, myLocation, hideSearchBar, stations, searchTarget]);

  const clearRoute = useCallback(() => {
    setRouteLatLngs([]);
    setRouteSummary(null);
    setAltRoutes([]);
    setSelectedRouteIndex(-1);
    setRouteError(null);
    setRouteDestination(null);
    setSearchTarget(null);
    setSearchValue('');
    setSuggestions([]);
    pendingRouteStationsRef.current = false;
    setPendingRouteStations(false);
    routeFetchObservedRef.current = false;
    // Don't reset routePlanner - keep it so user can reuse last entered data
    // Only update localStorage to remove route data but keep routePlanner
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed) {
          // Save only routePlanner, clear route data
          const routeState = {
            routeLatLngs: [],
            routeSummary: null,
            routeDestination: null,
            routePlanner: parsed.routePlanner || {
              start: { label: '', coords: null },
              destination: { label: '', coords: null },
              checkpoints: [],
            },
            altRoutes: [],
            selectedRouteIndex: -1,
          };
          localStorage.setItem('nsf_lastRoute', JSON.stringify(routeState));
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    showSearchBar();
  }, [showSearchBar]);

  const googleMapsDirectionsUrl = useCallback((dest: { lat: number; lng: number }) => {
    const origin = myLocation || center;
    const o = `${origin.lat},${origin.lng}`;
    const d = `${dest.lat},${dest.lng}`;
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(o)}&destination=${encodeURIComponent(d)}&travelmode=driving`;
  }, [myLocation, center]);

  const googleMapsUrlForCurrentRoute = useCallback(() => {
    // Need route destination and at least a route calculated
    if (!routeDestination || routeLatLngs.length === 0) return null;
    
    // Determine origin - use routePlanner if available, otherwise use first point of route (most accurate)
    const originCoords = routePlanner.start.coords || (routeLatLngs.length > 0 ? routeLatLngs[0] : null) || myLocation || center;
    if (!originCoords) return null;
    
    const origin = `${originCoords.lat},${originCoords.lng}`;
    const destination = `${routeDestination.lat},${routeDestination.lng}`;
    
    // Include waypoints if using route planner
    const waypointList = routePlanner.checkpoints
      .filter(cp => !!cp.coords)
      .map(cp => `${cp.coords!.lat},${cp.coords!.lng}`);
    const waypointParam = waypointList.length ? `&waypoints=${encodeURIComponent(waypointList.join('|'))}` : '';
    
    return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypointParam}&travelmode=driving`;
  }, [routeDestination, routeLatLngs, routePlanner.checkpoints, routePlanner.start.coords, myLocation, center]);

  const fetchSuggestions = useCallback(async (q: string) => {
    if (!q || q.trim().length < 2) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    const query = q.trim();
    const byCode = new Map<string, QuikQStation>();

    const collectStation = (station: QuikQStation) => {
      if (!station.stationCode || typeof station.latitude !== 'number' || typeof station.longitude !== 'number') {
        return;
      }
      byCode.set(station.stationCode, station);
    };

    for (const station of stations) {
      if (scoreStationSearchMatch(station, query) > 0) {
        collectStation(station);
      }
    }

    try {
      const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=7&countrycodes=us,ca&q=${encodeURIComponent(query)}`;

      const [apiMatches, nominatimRes] = await Promise.all([
        fuelPricesApiService.searchStationsByName(query, { limit: 12 }),
        fetch(nominatimUrl, { headers: { 'Accept-Language': 'en' } }),
      ]);

      for (const station of apiMatches) {
        collectStation(station);
      }

      const rankedStations = filterStationSearchResults(Array.from(byCode.values()), query, 8);
      const hasExactStation = rankedStations.some((s) => scoreStationSearchMatch(s, query) === 100);
      const stationSuggestions = rankedStations.map((station) => ({
        label: `${station.stationName} — ${station.city}, ${station.state}`,
        lat: station.latitude,
        lon: station.longitude,
        kind: 'station' as const,
      }));

      const placeSuggestions: Array<{ label: string; lat: number; lon: number; kind: 'place' }> = [];
      if (!hasExactStation && nominatimRes.ok) {
        const data: Array<{ display_name: string; lat: string; lon: string }> = await nominatimRes.json();
        for (const d of data) {
          placeSuggestions.push({
            label: d.display_name,
            lat: parseFloat(d.lat),
            lon: parseFloat(d.lon),
            kind: 'place',
          });
        }
      }

      setSuggestions([...stationSuggestions, ...placeSuggestions].slice(0, 12));
    } catch {
      const rankedStations = filterStationSearchResults(Array.from(byCode.values()), query, 8);
      const stationSuggestions = rankedStations.map((station) => ({
        label: `${station.stationName} — ${station.city}, ${station.state}`,
        lat: station.latitude,
        lon: station.longitude,
        kind: 'station' as const,
      }));
      try {
        const nominatimUrl = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=7&countrycodes=us,ca&q=${encodeURIComponent(query)}`;
        const res = await fetch(nominatimUrl, { headers: { 'Accept-Language': 'en' } });
        if (res.ok) {
          const data: Array<{ display_name: string; lat: string; lon: string }> = await res.json();
          const placeSuggestions = data.map((d) => ({
            label: d.display_name,
            lat: parseFloat(d.lat),
            lon: parseFloat(d.lon),
            kind: 'place' as const,
          }));
          setSuggestions([...stationSuggestions, ...placeSuggestions].slice(0, 12));
        } else {
          setSuggestions(stationSuggestions);
        }
      } catch {
        setSuggestions(stationSuggestions);
      }
    } finally {
      setLoadingSuggestions(false);
    }
  }, [stations]);

  const debouncedFetchSuggestions = useCallback((q: string) => {
    if (fetchSuggestionsTimeoutRef.current) {
      clearTimeout(fetchSuggestionsTimeoutRef.current);
    }
    fetchSuggestionsTimeoutRef.current = setTimeout(() => fetchSuggestions(q), 400);
  }, [fetchSuggestions]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchValue(val);
    setShowSuggestions(true);
    
    // Log search query (but only if it's substantial, to avoid logging every keystroke)
    if (val.trim().length >= 3) {
      const currentUser = quikqAuthService.getCurrentUser();
      logger.debug(LogCategory.MAP, 'Map: Search query entered', {
        query: val.trim(),
        userId: currentUser?.userId
      });
    }
    
    debouncedFetchSuggestions(val);
  }, [debouncedFetchSuggestions]);

  // Calculate distance from a point to a line segment (using perpendicular distance if point projects onto segment, otherwise distance to nearest endpoint)
  const distanceToSegmentMiles = useCallback((point: { lat: number; lng: number }, segStart: { lat: number; lng: number }, segEnd: { lat: number; lng: number }): number => {
    // Convert to approximate Cartesian coordinates for simpler calculation
    // Using equirectangular projection (good enough for short distances)
    const toRad = (x: number) => (x * Math.PI) / 180;
    const R = 6371000; // Earth radius in meters
    
    const lat1 = toRad(segStart.lat);
    const lon1 = toRad(segStart.lng);
    const lat2 = toRad(segEnd.lat);
    const lon2 = toRad(segEnd.lng);
    const latP = toRad(point.lat);
    const lonP = toRad(point.lng);
    
    // Convert to approximate x,y coordinates (meters)
    const x1 = R * lon1 * Math.cos((lat1 + lat2) / 2);
    const y1 = R * lat1;
    const x2 = R * lon2 * Math.cos((lat1 + lat2) / 2);
    const y2 = R * lat2;
    const xP = R * lonP * Math.cos((lat1 + lat2) / 2);
    const yP = R * latP;
    
    // Vector from segStart to segEnd
    const dx = x2 - x1;
    const dy = y2 - y1;
    const segLenSq = dx * dx + dy * dy;
    
    if (segLenSq === 0) {
      // Segment is a point
      return distanceMiles(point, segStart);
    }
    
    // Vector from segStart to point
    const dxP = xP - x1;
    const dyP = yP - y1;
    
    // Project point onto line segment
    const t = Math.max(0, Math.min(1, (dxP * dx + dyP * dy) / segLenSq));
    
    // Closest point on segment
    const closestX = x1 + t * dx;
    const closestY = y1 + t * dy;
    
    // Distance from point to closest point on segment
    const distX = xP - closestX;
    const distY = yP - closestY;
    const distMeters = Math.sqrt(distX * distX + distY * distY);
    
    return distMeters / 1609.34; // Convert to miles
  }, [distanceMiles]);

  // Calculate distance from a point to the nearest point on a route (polyline)
  const distanceToRouteMiles = useCallback((point: { lat: number; lng: number }, route: Array<{ lat: number; lng: number }>): number => {
    if (route.length === 0) return Infinity;
    if (route.length === 1) return distanceMiles(point, route[0]);
    
    let minDistance = Infinity;
    
    // Check distance to each segment of the route
    for (let i = 0; i < route.length - 1; i++) {
      const segStart = route[i];
      const segEnd = route[i + 1];
      
      // Calculate distance from point to line segment
      const dist = distanceToSegmentMiles(point, segStart, segEnd);
      if (dist < minDistance) {
        minDistance = dist;
      }
    }
    
    return minDistance;
  }, [distanceMiles, distanceToSegmentMiles]);

  const handleLocate = useCallback(() => {
    const doLocate = (coords: { lat: number; lng: number }) => {
      isLocatingRef.current = true; // Set flag to indicate we're locating
      setMyLocation(coords);
      setCenter(coords);
      setCenterForSuggestions(coords);
      centerForSuggestionsRef.current = coords;
      setShowLocationPrompt(false);
      setLocationPromptMessage(null);
      // Save to localStorage
      try {
        localStorage.setItem('nsf_lastLocation', JSON.stringify(coords));
      } catch (e) {
        // Ignore localStorage errors
      }
      if (mapRef.current) {
        const map = mapRef.current;
        const currentZoom = map.getZoom();
        const targetZoom = Math.max(typeof currentZoom === 'number' ? currentZoom : 12, 12);

        // Clear locating flag after movement settles
        map.once('moveend', () => {
          isLocatingRef.current = false;
        });

        map.flyTo({ center: [coords.lng, coords.lat], zoom: targetZoom, essential: true });

        // Fallback: if no moveend fires (e.g., already at location), schedule fetch
        setTimeout(() => {
          if (mapRef.current && isLocatingRef.current) {
            const currentCenter = mapRef.current.getCenter();
            const distanceMoved = distanceMiles(coords, { lat: currentCenter.lat, lng: currentCenter.lng });
            if (distanceMoved < 0.01) {
              isLocatingRef.current = false; // Clear flag
              scheduleStationFetch('locate', 80);
            }
          }
        }, 800); // Increased timeout to ensure animation completes
      }
    };

    if (USE_TEST_LOCATION) {
      doLocate(TEST_LOCATION);
      return;
    }
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        doLocate({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      (error) => {
        // Surface errors so the user can fix permissions / device location settings
        const code = error?.code;
        if (code === error.PERMISSION_DENIED) {
          setGeoPermission('denied');
          setLocationPromptMessage('Location permission is blocked. Please enable location permission for this site in your browser settings.');
          setShowLocationPrompt(true);
        } else if (code === error.POSITION_UNAVAILABLE) {
          setLocationPromptMessage('Location is unavailable. Please turn on Location Services/GPS and try again.');
          setShowLocationPrompt(true);
        } else if (code === error.TIMEOUT) {
          setLocationPromptMessage('Location request timed out. Please make sure Location Services are ON and try again.');
          setShowLocationPrompt(true);
        } else {
          setLocationPromptMessage('Unable to get your location. Please check your device location settings and try again.');
          setShowLocationPrompt(true);
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );
  }, [distanceMiles, scheduleStationFetch]);

  const requestInitialLocation = useCallback((opts?: { force?: boolean; moveMap?: boolean }) => {
    const force = !!opts?.force;
    const moveMap = opts?.moveMap !== false; // default true

    // Keep permission state in sync when possible
    (async () => {
      try {
        if (!navigator?.permissions?.query) return;
        const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
        const state = (status?.state || 'unknown') as GeoPermissionState;
        setGeoPermission(state);
      } catch {
        // ignore
      }
    })();

    if (USE_TEST_LOCATION) {
      setShowLocationPrompt(false);
      setLocationPromptMessage(null);
      setMyLocation(TEST_LOCATION);
      setCenter(TEST_LOCATION);
      setCenterForSuggestions(TEST_LOCATION);
      centerForSuggestionsRef.current = TEST_LOCATION;
      if (mapRef.current && moveMap) {
        const map = mapRef.current;
        const currentZoom = map.getZoom();
        const targetZoom = Math.max(typeof currentZoom === 'number' ? currentZoom : 12, 12);
        map.flyTo({ center: [TEST_LOCATION.lng, TEST_LOCATION.lat], zoom: targetZoom, essential: true });
      }
      return;
    }

    if (!force) {
      // If we already have a saved location (from state init), keep UI clean (no Chicago flash)
      const fallback = myLocation;
      if (fallback) {
        setCenter(fallback);
        // Don't set centerForSuggestions here - only set on geolocation success or pan
      }
    }

    if (!navigator.geolocation) {
      setGeoPermission('unknown');
      if (!myLocation) {
        setLocationPromptMessage('Geolocation is not supported by this browser/device.');
        setShowLocationPrompt(true);
      }
      return;
    }

    setIsRequestingInitialLocation(true);
    setLocationPromptMessage(null);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setGeoPermission('granted');
        setShowLocationPrompt(false);
        setLocationPromptMessage(null);
        setMyLocation(coords);
        setCenter(coords);
        setCenterForSuggestions(coords);
        centerForSuggestionsRef.current = coords;

        if (mapRef.current && moveMap) {
          const map = mapRef.current;
          const currentZoom = map.getZoom();
          const targetZoom = Math.max(typeof currentZoom === 'number' ? currentZoom : 12, 12);
          map.flyTo({ center: [coords.lng, coords.lat], zoom: targetZoom, essential: true });
        }

        setIsRequestingInitialLocation(false);
        try {
          localStorage.setItem('nsf_lastLocation', JSON.stringify(coords));
        } catch {
          // ignore
        }
      },
      (error) => {
        setIsRequestingInitialLocation(false);

        const code = error?.code;
        if (code === error.PERMISSION_DENIED) {
          setGeoPermission('denied');
          setLocationPromptMessage('Location permission is blocked. Please enable location permission for this site in your browser settings.');
          if (!myLocation || force) setShowLocationPrompt(true);
          return;
        }

        // This often happens when device Location Services / GPS is OFF
        if (code === error.POSITION_UNAVAILABLE) {
          setLocationPromptMessage('Location is unavailable. Please turn on Location Services/GPS and try again.');
          if (!myLocation || force) setShowLocationPrompt(true);
          return;
        }

        if (code === error.TIMEOUT) {
          setLocationPromptMessage('Location request timed out. Please make sure Location Services are ON and try again.');
          if (!myLocation || force) setShowLocationPrompt(true);
          return;
        }

        setLocationPromptMessage('Unable to get your location. Please check your device location settings and try again.');
        if (!myLocation || force) setShowLocationPrompt(true);
      },
      { enableHighAccuracy: true, maximumAge: 30000, timeout: 8000 }
    );
  }, [myLocation]);

  const handleSuggestionSelect = useCallback((item: { label: string; lat: number; lon: number }) => {
    setSearchValue(item.label);
    setShowSuggestions(false);
    const coords = { lat: item.lat, lng: item.lon };
    setSearchTarget({ ...coords, label: item.label });
    setCenter(coords);
    setCenterForSuggestions(coords); // Update suggested stations center
    
    // Log search selection
    const currentUser = quikqAuthService.getCurrentUser();
    logger.logUserAction('Map: Search location selected', {
      location: item.label,
      coordinates: coords,
      userId: currentUser?.userId
    });
    centerForSuggestionsRef.current = coords;
    if (mapRef.current) {
      const map = mapRef.current;
      const currentZoom = map.getZoom();
      const targetZoom = Math.max(typeof currentZoom === 'number' ? currentZoom : 12, 12);

      map.flyTo({ center: [coords.lng, coords.lat], zoom: targetZoom, essential: true });

      // Fallback: if no moveend fires, schedule after short delay
      setTimeout(() => {
        if (mapRef.current) {
          const currentCenter = mapRef.current.getCenter();
          const distanceMoved = distanceMiles(coords, { lat: currentCenter.lat, lng: currentCenter.lng });
          if (distanceMoved < 0.01) {
            scheduleStationFetch('search', 80);
          }
        }
      }, 100);
    } else {
      scheduleStationFetch('search', 80);
    }
  }, [distanceMiles, scheduleStationFetch]);

  const fetchPlannerSuggestions = useCallback(async (q: string) => {
    if (!q) {
      setPlannerSuggestions([]);
      return;
    }
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=7&countrycodes=us,ca&q=${encodeURIComponent(q)}`;
      const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
      if (!res.ok) throw new Error('Failed');
      const data: Array<{ display_name: string; lat: string; lon: string } > = await res.json();
      const mapped = data.map(d => ({ label: d.display_name, lat: parseFloat(d.lat), lon: parseFloat(d.lon) }));
      setPlannerSuggestions(mapped);
    } catch {
      setPlannerSuggestions([]);
    }
  }, []);

  const debouncedFetchPlannerSuggestions = useCallback((q: string) => {
    if (plannerSuggestionsTimeoutRef.current) {
      clearTimeout(plannerSuggestionsTimeoutRef.current);
    }
    plannerSuggestionsTimeoutRef.current = setTimeout(() => fetchPlannerSuggestions(q), 350);
  }, [fetchPlannerSuggestions]);

  const updatePlannerField = useCallback((type: 'start' | 'destination' | 'checkpoint', value: string, id?: string) => {
    setPlannerActiveField({ type, id });
    setRoutePlanner(prev => {
      if (type === 'start') {
        return { ...prev, start: { label: value, coords: null } };
      }
      if (type === 'destination') {
        return { ...prev, destination: { label: value, coords: null } };
      }
      return {
        ...prev,
        checkpoints: prev.checkpoints.map(cp => cp.id === id ? { ...cp, label: value, coords: null } : cp),
      };
    });
    debouncedFetchPlannerSuggestions(value);
  }, [debouncedFetchPlannerSuggestions]);

  const resolvePlannerSuggestion = useCallback((suggestion: { label: string; lat: number; lon: number }) => {
    if (!plannerActiveField) return;
    const coords = { lat: suggestion.lat, lng: suggestion.lon };
    const label = suggestion.label;
    setRoutePlanner(prev => {
      if (plannerActiveField.type === 'start') {
        return { ...prev, start: { label, coords } };
      }
      if (plannerActiveField.type === 'destination') {
        return { ...prev, destination: { label, coords } };
      }
      return {
        ...prev,
        checkpoints: prev.checkpoints.map(cp => cp.id === plannerActiveField.id ? { ...cp, label, coords } : cp),
      };
    });
    setPlannerActiveField(null);
    setPlannerSuggestions([]);
    setPlannerAnchorEl(null);
  }, [plannerActiveField]);

  const addPlannerCheckpoint = () => {
    const id = `${Date.now()}`;
    setRoutePlanner(prev => ({
      ...prev,
      checkpoints: [...prev.checkpoints, { id, label: '', coords: null }],
    }));
    // Focus popup state on the newly added (empty) checkpoint
    setActiveCheckpointPopupId(id);
  };

  const removePlannerCheckpoint = useCallback(async (id: string) => {
    // Use functional update to get the latest state
    let updatedCheckpoints: Array<{ id: string; label: string; coords: { lat: number; lng: number } | null }> = [];
    let currentStartCoords: { lat: number; lng: number } | null = null;
    
    setRoutePlanner(prev => {
      updatedCheckpoints = prev.checkpoints.filter(cp => cp.id !== id);
      currentStartCoords = prev.start.coords;
      return {
        ...prev,
        checkpoints: updatedCheckpoints,
      };
    });

    // If route already exists, rebuild it without the removed checkpoint
    if (routeLatLngs.length > 0 && routeDestination) {
      // Determine origin - use routePlanner start if available, otherwise use first point of route
      const origin = currentStartCoords || (routeLatLngs.length > 0 ? routeLatLngs[0] : null) || myLocation || center;
      
      if (!origin) return;

      const waypointCoords = updatedCheckpoints
        .filter(cp => cp.coords)
        .map(cp => cp.coords as { lat: number; lng: number });
      
      const allCoords = [origin, ...waypointCoords, routeDestination];
      const coordString = allCoords.map(c => `${c.lng},${c.lat}`).join(';');

      setRouteLoading(true);
      setRouteError(null);

      const endpoints = [
        'https://router.project-osrm.org',
        'https://routing.openstreetmap.de/routed-car'
      ];

      let routeLatLngsResult: Array<{ lat: number; lng: number }> | null = null;
      let summaryResult: { distanceMi: number; durationMin: number } | null = null;
      let lastError: Error | null = null;

      for (const base of endpoints) {
        try {
          const url = `${base}/route/v1/driving/${coordString}?overview=full&alternatives=0&geometries=geojson`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          const res = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`Route service error: ${res.status}`);
          const data = await res.json();
          const route = Array.isArray(data?.routes) && data.routes[0] ? data.routes[0] : null;
          if (!route) {
            lastError = new Error('No route');
            continue;
          }
          const routeCoords: Array<[number, number]> = route.geometry?.coordinates || [];
          routeLatLngsResult = routeCoords.map(([lng, lat]) => ({ lat, lng }));
          summaryResult = {
            distanceMi: route.distance / 1609.34,
            durationMin: route.duration / 60,
          };
          break;
        } catch (err: any) {
          lastError = err instanceof Error ? err : new Error('Unknown routing error');
        }
      }

      if (!routeLatLngsResult || !summaryResult) {
        setRouteLoading(false);
        setRouteError(lastError?.message || 'Could not recalculate route after removing stop.');
        return;
      }

      setAltRoutes([]);
      setSelectedRouteIndex(0);
      setRouteLatLngs(routeLatLngsResult);
      setRouteSummary(summaryResult);

      setRouteLoading(false);
    }
  }, [routeLatLngs, routeDestination, myLocation, center, isMapReady]);

  const isStationCheckpoint = useCallback((station: QuikQStation): string | null => {
    const coords = { lat: station.latitude, lng: station.longitude };
    
    // Check if this station is a checkpoint
    const checkpoint = routePlanner.checkpoints.find(cp => 
      cp.coords && 
      Math.abs(cp.coords.lat - coords.lat) < 0.0001 && 
      Math.abs(cp.coords.lng - coords.lng) < 0.0001
    );
    
    if (checkpoint) {
      return checkpoint.id;
    }
    
    return null;
  }, [routePlanner.checkpoints]);

  const addStationAsCheckpoint = useCallback(async (station: QuikQStation) => {
    const coords = { lat: station.latitude, lng: station.longitude };
    const label = station.stationName || `${station.city}, ${station.state}`;
    
    // Check if this station is already a checkpoint
    const checkpointId = isStationCheckpoint(station);
    if (checkpointId) {
      // Station is already a checkpoint, don't add duplicate
      return;
    }
    
    // Add checkpoint to route planner
    const newCheckpoint = { id: `${Date.now()}`, label, coords };
    setRoutePlanner(prev => ({
      ...prev,
      checkpoints: [...prev.checkpoints, newCheckpoint],
    }));
    // Kada dodamo stanicu kao stop:
    // - zatvorimo popup stanice
    // - fokusiramo se na popup za novi stop (bez preklapanja dva popupa)
    // - vratimo Suggested nearby i search bar da ne ostanu "zaglavljeni" sakriveni
    setOpenStationCode(null);
    setActiveCheckpointPopupId(newCheckpoint.id);
    // Do not expand Suggested nearby or show search bar when adding a stop

    // If route already exists, rebuild it with the new checkpoint
    if (routeLatLngs.length > 0 && routeDestination) {
      // Determine origin - use routePlanner start if available, otherwise use first point of route
      const origin = routePlanner.start.coords || (routeLatLngs.length > 0 ? routeLatLngs[0] : null) || myLocation || center;
      
      if (!origin) return;

      const waypointCoords = [...routePlanner.checkpoints, newCheckpoint]
        .filter(cp => cp.coords)
        .map(cp => cp.coords as { lat: number; lng: number });
      
      const allCoords = [origin, ...waypointCoords, routeDestination];
      const coordString = allCoords.map(c => `${c.lng},${c.lat}`).join(';');

      setRouteLoading(true);
      setRouteError(null);

      const endpoints = [
        'https://router.project-osrm.org',
        'https://routing.openstreetmap.de/routed-car'
      ];

      let routeLatLngsResult: Array<{ lat: number; lng: number }> | null = null;
      let summaryResult: { distanceMi: number; durationMin: number } | null = null;
      let lastError: Error | null = null;

      for (const base of endpoints) {
        try {
          const url = `${base}/route/v1/driving/${coordString}?overview=full&alternatives=0&geometries=geojson`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          const res = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`Route service error: ${res.status}`);
          const data = await res.json();
          const route = Array.isArray(data?.routes) && data.routes[0] ? data.routes[0] : null;
          if (!route) {
            lastError = new Error('No route');
            continue;
          }
          const routeCoords: Array<[number, number]> = route.geometry?.coordinates || [];
          routeLatLngsResult = routeCoords.map(([lng, lat]) => ({ lat, lng }));
          summaryResult = {
            distanceMi: route.distance / 1609.34,
            durationMin: route.duration / 60,
          };
          break;
        } catch (err: any) {
          lastError = err instanceof Error ? err : new Error('Unknown routing error');
        }
      }

      if (!routeLatLngsResult || !summaryResult) {
        setRouteLoading(false);
        setRouteError(lastError?.message || 'Could not recalculate route with new checkpoint.');
        return;
      }

      setAltRoutes([]);
      setSelectedRouteIndex(0);
      setRouteLatLngs(routeLatLngsResult);
      setRouteSummary(summaryResult);

      fitBoundsToRoute(routeLatLngsResult);

      setRouteLoading(false);
    }
  }, [routeLatLngs, routeDestination, routePlanner.start.coords, routePlanner.checkpoints, myLocation, center, isStationCheckpoint, isMapReady, fitBoundsToRoute]);

  const removeStationAsCheckpoint = useCallback(async (station: QuikQStation) => {
    const checkpointId = isStationCheckpoint(station);
    if (!checkpointId) {
      return;
    }

    // Remove checkpoint from route planner
    setRoutePlanner(prev => ({
      ...prev,
      checkpoints: prev.checkpoints.filter(cp => cp.id !== checkpointId),
    }));

    // If route already exists, rebuild it without the removed checkpoint
    if (routeLatLngs.length > 0 && routeDestination) {
      // Determine origin - use routePlanner start if available, otherwise use first point of route
      const origin = routePlanner.start.coords || (routeLatLngs.length > 0 ? routeLatLngs[0] : null) || myLocation || center;
      
      if (!origin) return;

      const waypointCoords = routePlanner.checkpoints
        .filter(cp => cp.coords && cp.id !== checkpointId)
        .map(cp => cp.coords as { lat: number; lng: number });
      
      const allCoords = [origin, ...waypointCoords, routeDestination];
      const coordString = allCoords.map(c => `${c.lng},${c.lat}`).join(';');

      setRouteLoading(true);
      setRouteError(null);

      const endpoints = [
        'https://router.project-osrm.org',
        'https://routing.openstreetmap.de/routed-car'
      ];

      let routeLatLngsResult: Array<{ lat: number; lng: number }> | null = null;
      let summaryResult: { distanceMi: number; durationMin: number } | null = null;
      let lastError: Error | null = null;

      for (const base of endpoints) {
        try {
          const url = `${base}/route/v1/driving/${coordString}?overview=full&alternatives=0&geometries=geojson`;
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000);
          const res = await fetch(url, { signal: controller.signal });
          clearTimeout(timeoutId);
          if (!res.ok) throw new Error(`Route service error: ${res.status}`);
          const data = await res.json();
          const route = Array.isArray(data?.routes) && data.routes[0] ? data.routes[0] : null;
          if (!route) {
            lastError = new Error('No route');
            continue;
          }
          const routeCoords: Array<[number, number]> = route.geometry?.coordinates || [];
          routeLatLngsResult = routeCoords.map(([lng, lat]) => ({ lat, lng }));
          summaryResult = {
            distanceMi: route.distance / 1609.34,
            durationMin: route.duration / 60,
          };
          break;
        } catch (err: any) {
          lastError = err instanceof Error ? err : new Error('Unknown routing error');
        }
      }

      if (!routeLatLngsResult || !summaryResult) {
        setRouteLoading(false);
        setRouteError(lastError?.message || 'Could not recalculate route without checkpoint.');
        return;
      }

      setAltRoutes([]);
      setSelectedRouteIndex(0);
      setRouteLatLngs(routeLatLngsResult);
      setRouteSummary(summaryResult);

      fitBoundsToRoute(routeLatLngsResult);

      setRouteLoading(false);
    }
  }, [routeLatLngs, routeDestination, routePlanner.start.coords, routePlanner.checkpoints, myLocation, center, isStationCheckpoint, isMapReady, fitBoundsToRoute]);

  const closePlanRoute = useCallback(() => {
    setIsPlanRouteOpen(false);
    setPlannerSuggestions([]);
    setPlannerAnchorEl(null);
    showSearchBar();
  }, [showSearchBar]);

  const resetPlanRoute = useCallback(() => {
    setRoutePlanner({
      start: { label: '', coords: null },
      destination: { label: '', coords: null },
      checkpoints: [],
    });
    setPlannerSuggestions([]);
    setPlannerActiveField(null);
    setPlannerAnchorEl(null);
    // Clear routePlanner from localStorage
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed) {
          // Clear routePlanner, keep other route data if exists
          const routeState = {
            routeLatLngs: parsed.routeLatLngs || [],
            routeSummary: parsed.routeSummary || null,
            routeDestination: parsed.routeDestination || null,
            routePlanner: {
              start: { label: '', coords: null },
              destination: { label: '', coords: null },
              checkpoints: [],
            },
            altRoutes: parsed.altRoutes || [],
            selectedRouteIndex: parsed.selectedRouteIndex !== undefined ? parsed.selectedRouteIndex : -1,
          };
          localStorage.setItem('nsf_lastRoute', JSON.stringify(routeState));
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }, []);

  const openPlanRoute = useCallback(() => {
    // Restore routePlanner from localStorage if it exists
    try {
      const saved = localStorage.getItem('nsf_lastRoute');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.routePlanner) {
          const savedPlanner = parsed.routePlanner;
          // Restore if saved planner has any data (start, destination, or checkpoints)
          if (savedPlanner.start.coords || savedPlanner.destination.coords || savedPlanner.checkpoints.length > 0) {
            setRoutePlanner(savedPlanner);
          }
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    hideSearchBar();
    setIsPlanRouteOpen(true);
  }, [hideSearchBar]);

  // When plan route dialog opens, default start to current location if start has no coords
  React.useEffect(() => {
    if (!isPlanRouteOpen) return;
    if (routePlanner.start.coords) return;
    const coords = myLocation || center;
    if (!coords) return;
    setRoutePlanner((prev) => ({
      ...prev,
      start: { label: 'My location', coords },
    }));
  }, [isPlanRouteOpen, myLocation, center]);

  const plannerCheckpointMarkers = useMemo(() => {
    return routePlanner.checkpoints.filter((cp) => !!cp.coords) as Array<{ id: string; label: string; coords: { lat: number; lng: number } }>;
  }, [routePlanner.checkpoints]);

  const plannerStartMarker = routePlanner.start.coords
    ? { coords: routePlanner.start.coords, label: routePlanner.start.label || 'Start' }
    : null;

  const primaryRouteGeoJson = useMemo<FeatureCollection<LineString> | null>(() => {
    if (routeLatLngs.length === 0) return null;
    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: routeLatLngs.map((p) => [p.lng, p.lat]),
          },
        },
      ],
    };
  }, [routeLatLngs]);

  const altRoutesGeoJson = useMemo<FeatureCollection<LineString> | null>(() => {
    if (altRoutes.length === 0) return null;
    return {
      type: 'FeatureCollection',
      features: altRoutes.map((r, idx) => ({
        type: 'Feature',
        properties: { routeIndex: idx },
        geometry: {
          type: 'LineString',
          coordinates: r.latlngs.map((p) => [p.lng, p.lat]),
        },
      })),
    };
  }, [altRoutes]);

  const resolveLabel = useCallback(async (coords: { lat: number; lng: number }) => {
    try {
      const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${coords.lat}&lon=${coords.lng}&addressdetails=1`, {
        headers: { 'Accept-Language': 'en' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data?.display_name) {
          return data.display_name as string;
        }
      }
    } catch {
      // ignore
    }
    return `${coords.lat.toFixed(5)}, ${coords.lng.toFixed(5)}`;
  }, []);

  const geocodeLabel = useCallback(async (label: string) => {
    const q = label.trim();
    if (!q) return null;
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(q)}&limit=1&addressdetails=1`,
        { headers: { 'Accept-Language': 'en' } }
      );
      if (!res.ok) return null;
      const data = await res.json();
      const first = Array.isArray(data) ? data[0] : null;
      const lat = first?.lat ? Number(first.lat) : NaN;
      const lon = first?.lon ? Number(first.lon) : NaN;
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
      return { lat, lng: lon };
    } catch {
      return null;
    }
  }, []);

  const handleCurrentLocationForPlanner = useCallback(async () => {
    let coords: { lat: number; lng: number } | null = myLocation || null;

    if (!coords && navigator.geolocation) {
      coords = await new Promise<{ lat: number; lng: number } | null>((resolve) => {
        const timeout = setTimeout(() => resolve(null), 5000);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            clearTimeout(timeout);
            resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          },
          () => {
            clearTimeout(timeout);
            resolve(null);
          },
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
        );
      });
    }

    if (!coords) {
      coords = center;
    }

    const label = await resolveLabel(coords);
    setRoutePlanner(prev => ({ ...prev, start: { label, coords } }));
  }, [myLocation, center, resolveLabel]);

  const buildPlannedRoute = useCallback(async () => {
    // Use start coords if set. If start is empty or "My location", use actual device/center location.
    // Only geocode when user has explicitly entered a different address.
    let origin = routePlanner.start.coords;
    const startLabel = (routePlanner.start.label || '').trim();
    const isMyLocationIntent = !startLabel || startLabel.toLowerCase() === 'my location';
    if (!origin && startLabel && !isMyLocationIntent) {
      const geocoded = await geocodeLabel(routePlanner.start.label);
      if (geocoded) {
        origin = geocoded;
        setRoutePlanner((prev) => ({ ...prev, start: { ...prev.start, coords: geocoded } }));
      }
    }
    if (!origin) origin = myLocation || center;

    let destination = routePlanner.destination.coords;
    if (!destination && routePlanner.destination.label.trim()) {
      const geocoded = await geocodeLabel(routePlanner.destination.label);
      if (geocoded) {
        destination = geocoded;
        setRoutePlanner((prev) => ({ ...prev, destination: { ...prev.destination, coords: geocoded } }));
      }
    }

    if (!origin || !destination) {
      setRouteError('Please enter both start and destination.');
      return;
    }
    const waypointCoords = routePlanner.checkpoints
      .filter(cp => cp.coords)
      .map(cp => cp.coords as { lat: number; lng: number });

    const allCoords = [origin, ...waypointCoords, destination];
    const coordString = allCoords.map(c => `${c.lng},${c.lat}`).join(';');

    setRouteLoading(true);
    setIsPlannerSubmitting(true);
    setRouteError(null);
    hideSearchBar();

    const endpoints = [
      'https://router.project-osrm.org',
      'https://routing.openstreetmap.de/routed-car'
    ];

    let routeLatLngsResult: Array<{ lat: number; lng: number }> | null = null;
    let summaryResult: { distanceMi: number; durationMin: number } | null = null;
    let lastError: Error | null = null;

    for (const base of endpoints) {
      try {
        const url = `${base}/route/v1/driving/${coordString}?overview=full&alternatives=0&geometries=geojson`;

        // Use a longer timeout and explicit AbortError handling to avoid
        // confusing "signal is aborted without reason" messages
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 25000); // 25s timeout

        const res = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!res.ok) throw new Error(`Route service error: ${res.status}`);

        const data = await res.json();
        const route = Array.isArray(data?.routes) && data.routes[0] ? data.routes[0] : null;
        if (!route) {
          lastError = new Error('No route');
          continue;
        }
        const coords: Array<[number, number]> = route.geometry?.coordinates || [];
        routeLatLngsResult = coords.map(([lng, lat]) => ({ lat, lng }));
        summaryResult = {
          distanceMi: route.distance / 1609.34,
          durationMin: route.duration / 60,
        };
        break;
      } catch (err: any) {
        const e = err instanceof Error ? err : new Error('Unknown routing error');
        // If the AbortController timed out, surface a clearer message
        if ((e as any).name === 'AbortError') {
          lastError = new Error('Routing service took too long to respond (timeout).');
        } else {
          lastError = e;
        }
      }
    }

    if (!routeLatLngsResult || !summaryResult) {
      setRouteLoading(false);
      setIsPlannerSubmitting(false);
      setRouteError(lastError?.message || 'Could not calculate route. Try again.');
      return;
    }

    setAltRoutes([]);
    setSelectedRouteIndex(0);
    setRouteLatLngs(routeLatLngsResult);
    setRouteSummary(summaryResult);
    setRouteDestination({ ...destination, label: routePlanner.destination.label || 'Destination' });
    setShowRouteStartPopup(true);
    setShowRouteDestinationPopup(true);
    routeFetchObservedRef.current = false;
    pendingRouteStationsRef.current = true;
    setPendingRouteStations(true);

    fitBoundsToRoute(routeLatLngsResult);

    setIsPlanRouteOpen(false);
    showSearchBar();
    setRouteLoading(false);
    setIsPlannerSubmitting(false);
  }, [center, hideSearchBar, myLocation, routePlanner, showSearchBar, geocodeLabel]);

  // De-dupe stations: same physical address (or same coords if address is incomplete) -> one marker
  const stationsUnique = React.useMemo(
    () => dedupeStationsByKey(stations, stationDedupeScore),
    [stations]
  );

  // Filter stations based on route when route exists, otherwise show all loaded stations
  const filteredStations = React.useMemo(() => {
    if (routeLatLngs.length === 0) {
      return stationsUnique;
    }

    // Route exists - filter stations within range of route
    const MAX_DISTANCE_FROM_ROUTE_MILES = 3; // Maximum distance from route in miles

    return stationsUnique.filter(station => {
      const stationPoint = { lat: station.latitude, lng: station.longitude };
      const distToRoute = distanceToRouteMiles(stationPoint, routeLatLngs);
      return distToRoute <= MAX_DISTANCE_FROM_ROUTE_MILES;
    });
  }, [stationsUnique, routeLatLngs, distanceToRouteMiles]);

  // Viewport culling: render only the markers that fall inside the visible
  // map bounds (plus a small padding so markers don't pop in/out at the edge).
  // With thousands of stations cached this dramatically reduces the number of
  // React/DOM marker nodes and keeps panning/zooming smooth even on laptops.
  const visibleStations = React.useMemo(() => {
    if (!viewportBounds) return filteredStations;
    const latPad = (viewportBounds.north - viewportBounds.south) * 0.15;
    const lngPad = (viewportBounds.east - viewportBounds.west) * 0.15;
    const south = viewportBounds.south - latPad;
    const north = viewportBounds.north + latPad;
    const west = viewportBounds.west - lngPad;
    const east = viewportBounds.east + lngPad;
    const crossesAntimeridian = west > east;
    return filteredStations.filter((s) => {
      const lat = s.latitude;
      const lng = s.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') return false;
      if (lat < south || lat > north) return false;
      return crossesAntimeridian ? (lng >= west || lng <= east) : (lng >= west && lng <= east);
    });
  }, [filteredStations, viewportBounds]);

  const suggestedStations = React.useMemo(() => {
    // Use centerForSuggestions which only updates on pan, not zoom
    const anchorPoint = centerForSuggestions;
    if (!anchorPoint) return [] as Array<{ s: QuikQStation; d: number; p: number }>;
    const bestPrice = (s: QuikQStation): number => {
      const list: any[] = Array.isArray((s as any).repricing) ? (s as any).repricing : [];
      if (!list.length) return Number.POSITIVE_INFINITY;
      let min = Number.POSITIVE_INFINITY;
      for (const r of list) {
        const code = String(r?.prodCode || '');
        const rowPrice = getDisplayPriceWithOverride(
          s.stationCode,
          code,
          r?.adjPrice,
          r?.retailPrice,
          priceOverridesMap
        );
        if (!shouldShowRepricingRow(code, rowPrice)) continue;
        const n = Number(rowPrice);
        if (Number.isFinite(n) && n < min) min = n;
      }
      if (min === Number.POSITIVE_INFINITY) return Number.POSITIVE_INFINITY;
      return min;
    };
    const withMetrics = filteredStations.map(s => ({
      s,
      d: distanceMiles({ lat: s.latitude, lng: s.longitude }, anchorPoint),
      p: bestPrice(s)
    }));
    return withMetrics
      .filter(
        x =>
          Number.isFinite(x.d) &&
          x.d <= 75
      )
      .sort((a, b) => {
        // Sort by distance first, then by best price ascending
        if (a.d !== b.d) return a.d - b.d;
        return a.p - b.p;
      })
      .slice(0, 5);
  }, [centerForSuggestions, filteredStations, distanceMiles]);

  // Save route state whenever it changes
  React.useEffect(() => {
    if (routeLatLngs.length > 0 || routePlanner.start.coords || routePlanner.destination.coords || routePlanner.checkpoints.length > 0) {
      saveRouteState();
    }
  }, [routeLatLngs, routeSummary, routeDestination, routePlanner, altRoutes, selectedRouteIndex, saveRouteState]);

  // Log route creation (once per new route; skip initial mount restore)
  React.useEffect(() => {
    if (!didMountRouteLogRef.current) {
      didMountRouteLogRef.current = true;
      return;
    }
    if (routeLatLngs.length < 2) return;

    const start = routePlanner.start.coords || routeLatLngs[0];
    const destination = routePlanner.destination.coords || routeLatLngs[routeLatLngs.length - 1];
    const routeKey = `${start.lat.toFixed(4)},${start.lng.toFixed(4)}->${destination.lat.toFixed(4)},${destination.lng.toFixed(4)}|pts:${routeLatLngs.length}|mi:${routeSummary?.distanceMi?.toFixed(1) ?? 'n/a'}|min:${routeSummary?.durationMin?.toFixed(0) ?? 'n/a'}`;
    if (lastRouteCreatedLogKeyRef.current === routeKey) return;
    lastRouteCreatedLogKeyRef.current = routeKey;

    logger.logUserAction('Map: Route created', {
      routeKey,
      selectedRouteIndex,
      checkpointsCount: routePlanner.checkpoints?.length || 0,
      start,
      destination,
      summary: routeSummary,
      routePointsCount: routeLatLngs.length
    });
  }, [routeLatLngs, routePlanner, routeSummary, selectedRouteIndex]);

  // Fetch on map ready and on moveend (only on pan, not zoom)
  React.useEffect(() => {
    if (!isMapReady) return;
    const map = mapRef.current;
    if (!map) return;
    lastZoomRef.current = map.getZoom();
    // Don't set centerForSuggestions initially - only on geolocation success or pan
    scheduleStationFetch('init', 0);

    // Initialize viewport bounds so the very first render already only paints
    // the markers that are visible on screen.
    try {
      const initialBounds = map.getBounds();
      setViewportBounds({
        south: initialBounds.getSouth(),
        west: initialBounds.getWest(),
        north: initialBounds.getNorth(),
        east: initialBounds.getEast(),
      });
    } catch {
      // Some maplibre versions may not expose getBounds until a frame has
      // rendered. The moveend handler below will fill this in shortly.
    }

    const moveendHandler = () => {
      if (!mapRef.current) return;

      const currentZoom = mapRef.current.getZoom();
      const currentCenter = mapRef.current.getCenter();
      try {
        const bounds = mapRef.current.getBounds();
        setViewportBounds({
          south: bounds.getSouth(),
          west: bounds.getWest(),
          north: bounds.getNorth(),
          east: bounds.getEast(),
        });
      } catch {
        // ignore — viewport filter just falls back to showing all stations
      }
      const prevZoom = lastZoomRef.current;
      const prevCenter = centerForSuggestionsRef.current;
      
      // Calculate distance moved
      let centerMoved = false;
      if (prevCenter) {
        const distanceMovedMiles = distanceMiles(
          { lat: prevCenter.lat, lng: prevCenter.lng },
          { lat: currentCenter.lat, lng: currentCenter.lng }
        );
        // Consider it moved if center changed by more than 0.1 miles (significant pan)
        centerMoved = distanceMovedMiles > 0.1;
      } else {
        // If no previous center, consider it moved to initialize
        centerMoved = true;
      }
      
      const zoomChanged = prevZoom !== null && prevZoom !== currentZoom;
      const wasZoomOnly = zoomChanged && !centerMoved;
      
      lastZoomRef.current = currentZoom;
      setCenter({ lat: currentCenter.lat, lng: currentCenter.lng });
      
      // Update suggested stations center only when center actually moves (pan), not on zoom
      if (centerMoved) {
        const newCenterForSuggestions = { lat: currentCenter.lat, lng: currentCenter.lng };
        setCenterForSuggestions(newCenterForSuggestions);
        centerForSuggestionsRef.current = newCenterForSuggestions;
      }
      
      // Only fetch stations if center moved significantly (pan), not just zoom
      if (centerMoved && !wasZoomOnly) {
        scheduleStationFetch('moveend', 250);
      }
    };
    
    map.on('moveend', moveendHandler);
    return () => {
      map.off('moveend', moveendHandler);
      if (fetchStationsTimeoutRef.current) {
        clearTimeout(fetchStationsTimeoutRef.current);
      }
      if (scheduleStationsTimeoutRef.current) {
        clearTimeout(scheduleStationsTimeoutRef.current);
      }
    };
  }, [isMapReady, distanceMiles, scheduleStationFetch]);

  // Fetch stations when myLocation changes (e.g., when user clicks "Locate me")
  React.useEffect(() => {
    if (!isMapReady || !myLocation) return;
    const map = mapRef.current;
    if (!map) return;
    // Wait a bit for map to finish animating to the new location
    const timeoutId = setTimeout(() => {
      scheduleStationFetch('locate', 120);
    }, 600);
    return () => clearTimeout(timeoutId);
  }, [myLocation, isMapReady, scheduleStationFetch]);

  // Fetch stations when route changes (to show stations along entire route).
  // Route-reason fetches don't depend on the map's current center (they sample
  // along the route geometry), so we skip the usual map-animation delay and
  // fire immediately to minimize the gap before the loading overlay reappears.
  React.useEffect(() => {
    if (!isMapReady || routeLatLngs.length === 0) return;
    const map = mapRef.current;
    if (!map) return;
    const timeoutId = setTimeout(() => {
      scheduleStationFetch('route', 0);
    }, 0);
    return () => clearTimeout(timeoutId);
  }, [routeLatLngs, isMapReady, scheduleStationFetch]);

  // Safety net: if the route-station fetch never actually runs (e.g. another
  // fetch held loadingRef for too long), make sure pendingRouteStations clears
  // so the user isn't stuck staring at the calculation animation forever.
  React.useEffect(() => {
    if (!pendingRouteStations) return;
    const safetyTimeout = setTimeout(() => {
      pendingRouteStationsRef.current = false;
      setPendingRouteStations(false);
      routeFetchObservedRef.current = false;
    }, 45000); // 45s hard cap
    return () => clearTimeout(safetyTimeout);
  }, [pendingRouteStations]);

  React.useEffect(() => {
    if (!isMapReady) return;
    // Only run initial location request once; otherwise the map keeps snapping back
    // (because requestInitialLocation changes when myLocation changes).
    if (didRequestInitialLocationRef.current) return;
    didRequestInitialLocationRef.current = true;
    requestInitialLocation();
  }, [isMapReady, requestInitialLocation]);

  // Re-check location when the app regains focus (helps when user enables Location Services / permissions in settings)
  React.useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      if (showLocationPrompt) {
        requestInitialLocation({ force: true, moveMap: true });
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [showLocationPrompt, requestInitialLocation]);

  // Socket.IO real-time updates
  React.useEffect(() => {
    if (hasInitializedSocketRef.current) return;
    hasInitializedSocketRef.current = true;

    // Connect to Socket.IO once
    socketService.connect();
    socketService.onConnect = () => setSocketConnected(true);
    socketService.onDisconnect = () => setSocketConnected(false);

    // Handle station updates
    const handleStationUpdate = (data: any) => {
      console.log('📍 Real-time station update received:', data);
      // Update stations in real-time
      if (data.station) {
        setStations(prev => {
          const existingIndex = prev.findIndex(s => s.stationCode === data.station.stationCode);
          if (existingIndex >= 0) {
            // Update existing station
            const updated = [...prev];
            updated[existingIndex] = { ...updated[existingIndex], ...data.station };
            return updated;
          } else {
            // Add new station if it's in the current view
            return [...prev, data.station];
          }
        });
      } else if (Array.isArray(data.stations)) {
        // Bulk update
        setStations(prev => {
          const updated = [...prev];
          data.stations.forEach((newStation: QuikQStation) => {
            const existingIndex = updated.findIndex(s => s.stationCode === newStation.stationCode);
            if (existingIndex >= 0) {
              updated[existingIndex] = { ...updated[existingIndex], ...newStation };
            } else {
              updated.push(newStation);
            }
          });
          return updated;
        });
      }
    };

    // Handle price updates
    const handlePriceUpdate = (data: { stationCode: string; repricing?: any[]; [key: string]: any }) => {
      console.log('💰 Real-time price update received:', data);
      setStations(prev => {
        const stationIndex = prev.findIndex(s => s.stationCode === data.stationCode);
        if (stationIndex >= 0) {
          const updated = [...prev];
          updated[stationIndex] = {
            ...updated[stationIndex],
            repricing: data.repricing || updated[stationIndex].repricing,
          };
          return updated;
        }
        return prev;
      });
    };

    socketService.onStationUpdate(handleStationUpdate);
    socketService.onPriceUpdate(handlePriceUpdate);

    return () => {
      socketService.offStationUpdate(handleStationUpdate);
      socketService.offPriceUpdate(handlePriceUpdate);
      socketService.onConnect = undefined;
      socketService.onDisconnect = undefined;
    };
  }, []);

  // When admin uploads new fuel_prices data, drop all local caches and refetch.
  React.useEffect(() => {
    const handleFuelPricesInvalidate = (data: { version: number; updatedAt: string; reason?: string }) => {
      console.info('fuel_prices dataset invalidated — refreshing map stations', data);
      void (async () => {
        await clearStationsCache();
        fuelPricesApiService.invalidateCaches();
        clearOverridesCache();
        stationDetailsCacheRef.current.clear();
        lastStationFetchKeyRef.current = '';
        lastStationFetchAtRef.current = 0;
        if (activeStationsAbortRef.current) {
          activeStationsAbortRef.current.abort();
        }
        const controller = new AbortController();
        activeStationsAbortRef.current = controller;
        try {
          await fetchStations({ signal: controller.signal, reason: 'init' });
        } catch (e: any) {
          if (e?.name !== 'AbortError') {
            console.warn('Station refresh after fuel_prices upload failed:', e);
          }
        }
      })();
    };

    socketService.onFuelPricesInvalidate(handleFuelPricesInvalidate);
    return () => {
      socketService.offFuelPricesInvalidate(handleFuelPricesInvalidate);
    };
  }, [fetchStations]);

  React.useEffect(() => {
    return () => {
      // React Strict Mode (dev) intentionally mounts/unmounts once to detect side effects.
      // Ignore the first cleanup so we don't disconnect an actively remounting socket.
      if (!hasMountedSocketCleanupRef.current) {
        hasMountedSocketCleanupRef.current = true;
        return;
      }
      socketService.unsubscribeFromStations();
      socketService.unsubscribeFromPrices();
      socketService.disconnect();
    };
  }, []);

  // Update Socket.IO subscriptions when map moves or stations change
  React.useEffect(() => {
    if (!isMapReady || !socketConnected || !socketService.connected || !mapRef.current) return;

    const map = mapRef.current;
    const bounds = map.getBounds();
    const center = map.getCenter();
    
    // Update station subscription
    socketService.subscribeToStations(
      bounds ? {
        north: bounds.getNorth(),
        south: bounds.getSouth(),
        east: bounds.getEast(),
        west: bounds.getWest(),
      } : null,
      { lat: center.lat, lng: center.lng }
    );

    // Update price subscription for visible stations
    if (stations.length > 0) {
      const stationCodes = stations.map(s => s.stationCode).filter(Boolean) as string[];
      if (stationCodes.length > 0) {
        socketService.subscribeToPrices(stationCodes);
      }
    }
  }, [center, isMapReady, stations, socketConnected]);

  const handleMapClick = useCallback(
    (event: any) => {
      const features = event.features || [];
      const routeFeature = features.find((f: any) => f.layer && f.layer.id === 'alt-routes-layer');

      // Click on alternative route line – keep existing behavior
      if (routeFeature) {
        const routeIndex = routeFeature.properties?.routeIndex;
        if (typeof routeIndex === 'number' && altRoutes[routeIndex]) {
          const r = altRoutes[routeIndex];
          setSelectedRouteIndex(routeIndex);
          setRouteLatLngs(r.latlngs);
          setRouteSummary({ distanceMi: r.distanceMi, durationMin: r.durationMin });
          fitBoundsToRoute(r.latlngs);
          return;
        }
      }
    },
    [altRoutes, fitBoundsToRoute]
  );

  return (
    <Box sx={{ 
      position: 'relative', 
      width: '100vw', 
      maxWidth: '100%',
      height: '100vh',
      overflow: 'hidden',
      margin: 0,
      padding: 0,
      left: 0,
      right: 0,
      boxSizing: 'border-box'
    }}>
      {/* Search Bar - Fixed and always visible */}
      {showLocationPrompt && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.45)',
            zIndex: 12000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Paper sx={{ p: 3, maxWidth: 420, width: '100%', textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              {geoPermission === 'denied' ? 'Enable location permission' : 'Turn on location to use the map'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {locationPromptMessage || 'We need your device location to center the map and show nearby stations.'}
            </Typography>
            <Button
              variant="contained"
              onClick={() => requestInitialLocation({ force: true, moveMap: true })}
              disabled={isRequestingInitialLocation}
              sx={{ bgcolor: '#1A5FB4', '&:hover': { bgcolor: '#0d47a1' } }}
            >
              {isRequestingInitialLocation ? 'Requesting...' : 'Enable location'}
            </Button>
          </Paper>
        </Box>
      )}

      {/* Hide the top search bar only while the route-calculation overlay is
          on screen, so it doesn't visually compete with the loading animation.
          Once stations finish loading it slides back in. */}
      {(() => {
        const isOverlayActive =
          routeLoading || pendingRouteStations || (loadingStations && stationsLoadReason === 'route');
        const isHidden = isSearchBarHidden || isOverlayActive;
        return (
      <Box sx={{ 
        position: 'absolute', 
        top: { xs: 80, sm: 90, md: 100 }, 
        left: { xs: 16, sm: 20, md: 20 }, 
        right: { xs: 16, sm: 20, md: 20 }, 
        zIndex: 10000, 
        pointerEvents: isHidden ? 'none' : 'auto',
        maxWidth: { md: '900px' },
        mx: { md: 'auto' },
        width: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 40px)', md: 'calc(100% - 40px)' },
        transition: 'opacity 0.3s ease, transform 0.3s ease',
        opacity: isHidden ? 0 : 1,
        transform: isHidden ? 'translateY(-80px)' : 'translateY(0)'
      }}>
        <style>
          {`
            @keyframes nsf-marker-fade-in {
              0% { transform: scale(0.8); opacity: 0; }
              60% { transform: scale(1.05); opacity: 1; }
              100% { transform: scale(1); opacity: 1; }
            }

            @keyframes nsf-popup-fade-in {
              0% { opacity: 0; }
              100% { opacity: 1; }
            }
          `}
        </style>
        <Paper sx={{ 
          p: { xs: '8px 12px', sm: '10px 16px' }, 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%', 
          boxSizing: 'border-box', 
          bgcolor: 'white !important',
          boxShadow: '0 4px 12px rgba(0,0,0,0.25) !important',
          borderRadius: 2,
          position: 'relative',
          zIndex: 10000,
          border: '1px solid rgba(0,0,0,0.1)'
        }} elevation={8}>
          <SearchIcon sx={{ color: '#1A5FB4', mr: 1, fontSize: 22, flexShrink: 0 }} />
          <InputBase
            placeholder="Search city, address, place or station"
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={() => setShowSuggestions(true)}
            sx={{ 
              flex: 1,
              fontSize: { xs: '14px', sm: '16px' },
              color: '#333',
              '& input': {
                padding: 0,
                '&::placeholder': {
                  color: '#999',
                  opacity: 1
                }
              }
            }}
          />
          {searchValue && (
            <IconButton
              size="small"
              onClick={() => {
                setSearchValue('');
                setSuggestions([]);
                setSearchTarget(null);
                setShowSuggestions(false);
              }}
              sx={{ color: '#777', ml: 0.5 }}
              aria-label="Clear search"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          {loadingSuggestions && <CircularProgress size={20} sx={{ ml: 1, flexShrink: 0, color: '#1A5FB4' }} />}
        </Paper>

        {showSuggestions && suggestions.length > 0 && (
          <Paper sx={{ mt: 1, width: '100%', maxHeight: 280, overflowY: 'auto' }} elevation={3}>
            <List dense>
              {suggestions.map((sug, idx) => (
                <ListItem key={`${sug.kind || 'place'}-${sug.label}-${idx}`} disablePadding>
                  <ListItemButton onClick={() => handleSuggestionSelect(sug)}>
                    <ListItemText
                      primary={sug.label}
                      secondary={sug.kind === 'station' ? 'Fuel station' : undefined}
                      primaryTypographyProps={{
                        fontWeight: sug.kind === 'station' ? 600 : 400,
                        fontSize: sug.kind === 'station' ? '14px' : undefined,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}

        {searchTarget && (
          <Paper sx={{ mt: 1.5, p: { xs: '10px 14px', sm: '12px 18px' }, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, alignItems: { sm: 'center' } }} elevation={4}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ fontWeight: 600, fontSize: { xs: '14px', sm: '15px' }, color: '#1A1A1A', mb: 0.5 }}>Selected location</Box>
              <Box sx={{ fontSize: { xs: '13px', sm: '14px' }, color: '#444', wordBreak: 'break-word' }}>{searchTarget.label || `${searchTarget.lat.toFixed(4)}, ${searchTarget.lng.toFixed(4)}`}</Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
              <button
                onClick={() => startDirectionsTo({ lat: searchTarget.lat, lng: searchTarget.lng })}
                disabled={routeLoading}
                style={{ padding: '8px 14px', background: '#1A5FB4', color: 'white', border: 'none', borderRadius: 6, cursor: routeLoading ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: routeLoading ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8 }}
              >
                {routeLoading && <CircularProgress size={16} sx={{ color: 'white' }} />}
                {routeLoading ? 'Calculating...' : 'Get Directions'}
              </button>
            </Box>
          </Paper>
        )}

        {routeLoading && (
          <Paper
            sx={{
              mt: 1.5,
              p: { xs: '10px 14px', sm: '12px 18px' },
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              bgcolor: '#e3f2fd',
              color: '#0d47a1',
              fontSize: { xs: '13px', sm: '14px' },
              border: '1px solid #bbdefb',
            }}
            elevation={0}
          >
            <CircularProgress size={16} sx={{ color: '#1A5FB4' }} />
            {isPlannerSubmitting ? 'Building your route, this can take a few seconds...' : 'Calculating directions, please wait...'}
          </Paper>
        )}

        {routeError && (
          <Paper sx={{ mt: 1.5, p: { xs: '10px 14px', sm: '12px 18px' }, bgcolor: '#ffebee', color: '#b71c1c', fontSize: { xs: '13px', sm: '14px' }, border: '1px solid #ffcdd2' }} elevation={0}>
            {routeError}
          </Paper>
        )}

        {/* bottom panel moved below, keep top area for search only */}
      </Box>
        );
      })()}

      {isPlanRouteOpen && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            bgcolor: 'rgba(0,0,0,0.45)',
            zIndex: 1400,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 1, sm: 2 },
          }}
        >
          <Paper
            elevation={6}
            sx={{
              width: '100%',
              maxWidth: 900,
              maxHeight: '90vh',
              overflow: 'auto',
              p: { xs: 2, sm: 3 },
              position: 'relative',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                Plan route
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                {(routePlanner.start.label || routePlanner.destination.label || routePlanner.checkpoints.length > 0) && (
                  <IconButton
                    onClick={resetPlanRoute}
                    size="small"
                    sx={{ color: '#666' }}
                    aria-label="Reset form"
                    title="Reset form"
                  >
                    <RefreshIcon />
                  </IconButton>
                )}
                <IconButton onClick={closePlanRoute}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Choose start, stops (optional) and destination.
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  label="Start"
                  fullWidth
                  size="small"
                  value={routePlanner.start.label}
                  onChange={(e) => updatePlannerField('start', e.target.value)}
                  onFocus={(e) => {
                    setPlannerActiveField({ type: 'start' });
                    setPlannerAnchorEl(e.currentTarget as unknown as HTMLElement);
                  }}
                  placeholder="Enter start address"
                />
                <IconButton
                  color="primary"
                  onClick={() => { void handleCurrentLocationForPlanner(); }}
                  sx={{ border: '1px solid #e0e0e0', bgcolor: 'white', width: 44, height: 40 }}
                  aria-label="Use my location"
                >
                  <MyLocationIcon />
                </IconButton>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {routePlanner.checkpoints.length > 0 && (
                  <>
                    {routePlanner.checkpoints.map((cp) => (
                      <Box key={cp.id} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                          label="Stop"
                          fullWidth
                          size="small"
                          value={cp.label || (cp.coords ? `${cp.coords.lat.toFixed(4)}, ${cp.coords.lng.toFixed(4)}` : '')}
                          onChange={(e) => updatePlannerField('checkpoint', e.target.value, cp.id)}
                          onFocus={(e) => {
                            setPlannerActiveField({ type: 'checkpoint', id: cp.id });
                            setPlannerAnchorEl(e.currentTarget as unknown as HTMLElement);
                          }}
                          placeholder="Add stop"
                          disabled={!!cp.coords}
                          sx={cp.coords ? { bgcolor: '#f5f5f5' } : {}}
                        />
                        <Button 
                          variant="outlined" 
                          color="error" 
                          onClick={() => { void removePlannerCheckpoint(cp.id); }}
                          disabled={routeLoading}
                        >
                          {routeLoading ? 'Removing...' : 'Remove'}
                        </Button>
                      </Box>
                    ))}
                  </>
                )}
                <Button variant="outlined" onClick={addPlannerCheckpoint} sx={{ alignSelf: 'flex-start' }}>
                  + Add stop
                </Button>
              </Box>

              <TextField
                label="Destination"
                fullWidth
                size="small"
                value={routePlanner.destination.label}
                onChange={(e) => updatePlannerField('destination', e.target.value)}
                onFocus={(e) => {
                  setPlannerActiveField({ type: 'destination' });
                  setPlannerAnchorEl(e.currentTarget as unknown as HTMLElement);
                }}
                placeholder="Enter destination"
              />
            </Box>

            <Popper
              open={plannerSuggestions.length > 0 && !!plannerAnchorEl}
              anchorEl={plannerAnchorEl}
              placement="bottom-start"
              modifiers={[
                { name: 'flip', enabled: true, options: { fallbackPlacements: ['top-start', 'bottom-start'] } },
                { name: 'preventOverflow', enabled: true, options: { padding: 12 } },
              ]}
              style={{ zIndex: 20000 }}
            >
              <Paper
                elevation={6}
                sx={{
                  mt: 0.5,
                  width: plannerAnchorEl ? `${plannerAnchorEl.clientWidth}px` : 360,
                  maxHeight: 280,
                  overflow: 'auto',
                }}
              >
                <List dense>
                  {plannerSuggestions.map((sug, idx) => (
                    <ListItemButton
                      key={`${sug.label}-${idx}`}
                      onClick={() => resolvePlannerSuggestion(sug)}
                    >
                      <ListItemText primary={sug.label} />
                    </ListItemButton>
                  ))}
                </List>
              </Paper>
            </Popper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button variant="text" onClick={closePlanRoute}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={buildPlannedRoute}
                disabled={isPlannerSubmitting}
              >
                {isPlannerSubmitting ? 'Creating...' : 'Create route'}
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      <MapLibreMap
        ref={mapRef}
        initialViewState={{
          longitude: center.lng,
          latitude: center.lat,
          zoom: 12,
        }}
        style={{
          ...containerStyle,
          height: '100%',
          width: '100%',
        }}
        mapStyle={MAP_STYLE}
        attributionControl={{
          compact: true,
          customAttribution:
            '&copy; <a href="https://openfreemap.org" target="_blank" rel="noopener noreferrer">OpenFreeMap</a> ' +
            '&copy; <a href="https://www.openmaptiles.org" target="_blank" rel="noopener noreferrer">OpenMapTiles</a> ' +
            '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
        }}
        interactiveLayerIds={altRoutesGeoJson ? ['alt-routes-layer'] : undefined}
        onClick={handleMapClick}
        onLoad={() => {
          setIsMapReady(true);
          if (USE_TEST_LOCATION && mapRef.current) {
            const map = mapRef.current;
            const currentZoom = map.getZoom();
            const safeZoom = typeof currentZoom === 'number' ? currentZoom : 12;
            map.setCenter([TEST_LOCATION.lng, TEST_LOCATION.lat]);
            map.setZoom(Math.max(safeZoom, 12));
          }
        }}
      >
        {/* My Location Marker - animated pulse */}
        {myLocation && (
          <Marker longitude={myLocation.lng} latitude={myLocation.lat} anchor="center">
            <style>
              {`
                @keyframes nsf-pulse {
                  0% { transform: translate(-50%, -50%) scale(0.6); opacity: 0.6; }
                  70% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
                  100% { transform: translate(-50%, -50%) scale(1.6); opacity: 0; }
                }
              `}
            </style>
            <div style={{ position: 'relative', width: 24, height: 24, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#1A5FB4',
                  border: '2px solid white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  zIndex: 2,
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'rgba(26,95,180,0.25)',
                  animation: 'nsf-pulse 1.8s ease-out infinite',
                  transformOrigin: '50% 50%',
                  willChange: 'transform, opacity',
                  zIndex: 1,
                }}
              />
            </div>
          </Marker>
        )}

        {/* Search target pin (non-station destination) */}
        {searchTarget && (
        <Marker
          longitude={searchTarget.lng}
          latitude={searchTarget.lat}
          anchor="bottom"
          style={{ zIndex: Z.SEARCH_TARGET }}
        >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', transform: 'translateY(-4px)' }}>
              {searchTarget.label && (
                <div
                  style={{
                    background: 'white',
                    borderRadius: 8,
                    padding: '8px 10px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                    marginBottom: 6,
                    minWidth: 160,
                    animation: 'nsf-popup-fade-in 0.18s ease-out',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{searchTarget.label}</div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      onClick={() => startDirectionsTo({ lat: searchTarget.lat, lng: searchTarget.lng })}
                      disabled={routeLoading}
                      style={{
                        padding: '6px 10px',
                        background: '#1A5FB4',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        cursor: routeLoading ? 'not-allowed' : 'pointer',
                        opacity: routeLoading ? 0.7 : 1,
                      }}
                    >
                      {routeLoading ? 'Calculating...' : 'Get Directions'}
                    </button>
                  </div>
                </div>
              )}
              <svg width="24" height="34" viewBox="0 0 24 34" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M12 0C5.924 0 1 4.924 1 11c0 6.5 7.5 14 10.2 22.2.2.6 1.4.6 1.6 0C15.5 25 23 17.5 23 11 23 4.924 18.076 0 12 0z"
                  fill="#E53935"
                  stroke="#FFFFFF"
                  strokeWidth="2"
                />
                <circle cx="12" cy="11" r="5" fill="#FFFFFF" />
              </svg>
            </div>
          </Marker>
        )}

        {/* Station Markers — viewport-culled so we only render what's on screen */}
        {visibleStations.map((s) => {
          // If this station is currently a checkpoint/stop, skip drawing the regular station icon.
          // The red "Stop" marker will represent it instead so icons don't overlap.
          const checkpointIdForStation = isStationCheckpoint(s);
          if (checkpointIdForStation) {
            return null;
          }
          const primaryBrand = s.affiliates?.find((a) => a.afType === 'BRAND')?.afCode || 'DEFAULT';
          const fuelNetworkName = getFuelNetworkDisplayName(s);
          const isOpen = openStationCode === s.stationCode;
          const baseZ = Z.NSF;
          const openZ = Z.STATION_POPUP_ADD_STOP;

          let iconContent: React.ReactNode;
          if (primaryBrand === 'TA') {
            iconContent = (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#1A5FB4',
                  border: '2px solid #FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  animation: 'nsf-marker-fade-in 0.25s ease-out',
                  transformOrigin: '50% 50%',
                }}
              >
                <span
                  style={{
                    color: 'white',
                    fontWeight: 700,
                    fontSize: 12,
                    fontFamily: 'Inter,system-ui,sans-serif',
                  }}
                >
                  TA
                </span>
              </div>
            );
          } else {
            iconContent = (
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: '#1A5FB4',
                  border: '2px solid #FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                  cursor: 'pointer',
                  animation: 'nsf-marker-fade-in 0.25s ease-out',
                  transformOrigin: '50% 50%',
                }}
              >
                <img src="/NSFuelWhite.png" alt="NSF" style={{ width: 26, height: 26, objectFit: 'contain' }} />
              </div>
            );
          }

          return (
            <Marker
              key={s.stationCode}
              longitude={s.longitude}
              latitude={s.latitude}
              anchor="center"
              style={{ zIndex: isOpen ? openZ : baseZ }}
              onClick={() => {
                const currentUser = quikqAuthService.getCurrentUser();
                logger.logUserAction('Map: Station clicked', {
                  stationName: s.stationName,
                  stationCode: s.stationCode,
                  coordinates: { lat: s.latitude, lng: s.longitude },
                  userId: currentUser?.userId,
                });
                setOpenStationCode(s.stationCode || null);
                // When a station popup is opened, collapse suggested list and hide search bar
                setSuggestedCollapsed(true);
                hideSearchBar();
                void loadStationDetailsOnDemand(s);
              }}
            >
              <div style={{ position: 'relative', transform: 'translateY(-8px)', cursor: 'pointer' }}>
                {isOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 36,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: 'white',
                      borderRadius: 8,
                      padding: 10,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                      minWidth: 220,
                      maxWidth: 280,
                      zIndex: 10,
                      animation: 'nsf-popup-fade-in 0.18s ease-out',
                    }}
                    onClick={(e) => {
                      // Prevent click on popup from bubbling to marker/map
                      e.stopPropagation();
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenStationCode(null);
                        showSearchBar();
                      }}
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 12,
                        lineHeight: 1,
                        color: '#444',
                      }}
                    >
                      ×
                    </button>
                    <div style={{ paddingTop: 8 }}>
                      {fuelNetworkName && (
                        <div
                          style={{
                            fontSize: 14,
                            color: '#1A5FB4',
                            fontWeight: 600,
                            marginBottom: 4,
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                          }}
                        >
                          {fuelNetworkName}
                        </div>
                      )}
                      <strong>{s.stationName}</strong>
                      <div>{s.address}</div>
                      <div>
                        {s.city}, {s.state} {s.zip}
                      </div>
                      {Array.isArray(s.repricing) && s.repricing.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <div style={{ fontWeight: 600, marginBottom: 4 }}>Prices</div>
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: '1fr auto',
                              gap: 6,
                              alignItems: 'center',
                            }}
                          >
                            {/* Render exactly two lines per station — Northstar
                                and Retail — derived from the prod-19 (Regular
                                Diesel) row. Every other fuel type returned by
                                QuikQ is intentionally hidden upstream by
                                `shouldShowRepricingRow`. The Northstar price
                                respects price-overrides; the Retail price is
                                taken raw from the QuikQ payload. */}
                            {s.repricing.map((item, idx) => {
                              const code = String(item.prodCode || '');
                              const { northstar: northstarPrice, retail: retailDisplay } = getStationCardPrices(
                                s,
                                s.stationCode,
                                code,
                                (item as any).adjPrice,
                                (item as any).retailPrice,
                                priceOverridesMap
                              );
                              if (!shouldShowRepricingRow(code, northstarPrice)) return null;

                              const showRetailPrice = isDualPriceNetworkStation(s)
                                ? northstarPrice != null
                                : retailDisplay != null;

                              return (
                                <React.Fragment key={`${s.stationCode}-${code || idx}`}>
                                  <span style={{ fontSize: 13, color: '#1A5FB4', fontWeight: 700 }}>
                                    Northstar price
                                  </span>
                                  <span
                                    style={{
                                      justifySelf: 'end',
                                      fontWeight: 800,
                                      fontSize: 15,
                                      color: '#1A5FB4',
                                    }}
                                  >
                                    ${Number(northstarPrice).toFixed(3)}
                                  </span>
                                  {showRetailPrice && retailDisplay != null && (
                                    <>
                                      <span style={{ fontSize: 12, color: '#777', fontWeight: 400 }}>
                                        Retail price
                                      </span>
                                      <span
                                        style={{
                                          justifySelf: 'end',
                                          fontWeight: 500,
                                          fontSize: 13,
                                          color: '#555',
                                        }}
                                      >
                                        ${Number(retailDisplay).toFixed(3)}
                                      </span>
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {/* Show "Get Directions" only if there's no planned route */}
                        {!(routePlanner.start.coords || routePlanner.destination.coords) && (
                          <button
                            onClick={() => startDirectionsTo({ lat: s.latitude, lng: s.longitude })}
                            disabled={routeLoading}
                            style={{
                              padding: '6px 10px',
                              background: '#1A5FB4',
                              color: 'white',
                              border: 'none',
                              borderRadius: 4,
                              cursor: routeLoading ? 'not-allowed' : 'pointer',
                              opacity: routeLoading ? 0.7 : 1,
                            }}
                          >
                            {routeLoading ? 'Calculating...' : 'Get Directions'}
                          </button>
                        )}
                        {/* Add as Stop / Remove as Stop only when there's a route and we work with this station; show nothing for non-partner stations */}
                        {(routePlanner.start.coords || routePlanner.destination.coords) &&
                          (() => {
                            const checkpointId = isStationCheckpoint(s);
                            const isCheckpoint = !!checkpointId;
                            return isCheckpoint ? (
                              <button
                                onClick={() => removeStationAsCheckpoint(s)}
                                disabled={routeLoading}
                                style={{
                                  padding: '6px 10px',
                                  background: '#DC3545',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  cursor: routeLoading ? 'not-allowed' : 'pointer',
                                  opacity: routeLoading ? 0.7 : 1,
                                }}
                              >
                                {routeLoading ? 'Removing...' : 'Remove as Stop'}
                              </button>
                            ) : (
                              <button
                                onClick={() => addStationAsCheckpoint(s)}
                                disabled={routeLoading}
                                style={{
                                  padding: '6px 10px',
                                  background: '#0F9D58',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: 4,
                                  cursor: routeLoading ? 'not-allowed' : 'pointer',
                                  opacity: routeLoading ? 0.7 : 1,
                                }}
                              >
                                {routeLoading ? 'Adding...' : 'Add as Stop'}
                              </button>
                            );
                          })()}
                      </div>
                    </div>
                  </div>
                )}
                {iconContent}
              </div>
            </Marker>
          );
        })}

        {/* Route Destination Marker */}
        {routeDestination && routeLatLngs.length > 0 && (
          <Marker
            longitude={routeDestination.lng}
            latitude={routeDestination.lat}
            anchor="bottom"
            style={{ zIndex: showRouteDestinationPopup ? Z.ROUTE_ENDPOINT_POPUP : Z.DESTINATION }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transform: 'translateY(-4px)',
              }}
            >
              {routeDestination.label && showRouteDestinationPopup && (
                <RouteEndpointPopup
                  title="Route destination"
                  label={routeDestination.label}
                  accentColor="#0F9D58"
                  onClose={() => setShowRouteDestinationPopup(false)}
                />
              )}
              <div
                role="button"
                aria-label="Toggle route destination details"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRouteDestinationPopup((v) => !v);
                }}
                style={{ cursor: 'pointer', lineHeight: 0 }}
              >
                <svg width="32" height="44" viewBox="0 0 24 34" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 0C5.924 0 1 4.924 1 11c0 6.5 7.5 14 10.2 22.2.2.6 1.4.6 1.6 0C15.5 25 23 17.5 23 11 23 4.924 18.076 0 12 0z"
                    fill="#0F9D58"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="11" r="5" fill="#FFFFFF" />
                </svg>
              </div>
            </div>
          </Marker>
        )}

        {plannerCheckpointMarkers.length > 0 &&
          routeLatLngs.length > 0 &&
          plannerCheckpointMarkers.map((cp) => (
            <Marker
              key={`cp-${cp.id}`}
              longitude={cp.coords.lng}
              latitude={cp.coords.lat}
              anchor="bottom"
              style={{ zIndex: activeCheckpointPopupId === cp.id ? Z.CHECKPOINT_POPUP : Z.CHECKPOINT }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  transform: 'translateY(-4px)',
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                {activeCheckpointPopupId === cp.id && (
                  <div
                    style={{
                      background: 'white',
                      borderRadius: 8,
                      padding: '8px 10px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                      marginBottom: 6,
                      minWidth: 160,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveCheckpointPopupId(null)}
                      style={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: 'none',
                        background: 'rgba(0,0,0,0.06)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        lineHeight: 1,
                        color: '#444',
                      }}
                    >
                      ×
                    </button>
                    <div style={{ maxWidth: 220, paddingTop: 4 }}>
                      <strong>Stop</strong>
                      <div style={{ marginTop: 4 }}>{cp.label}</div>
                      <div style={{ marginTop: 10 }}>
                        <button
                          onClick={() => {
                            void removePlannerCheckpoint(cp.id);
                          }}
                          disabled={routeLoading}
                          style={{
                            padding: '6px 10px',
                            background: '#DC3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: 4,
                            cursor: routeLoading ? 'not-allowed' : 'pointer',
                            opacity: routeLoading ? 0.7 : 1,
                            width: '100%',
                          }}
                        >
                          {routeLoading ? 'Removing...' : 'Remove as Stop'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  onClick={() =>
                    setActiveCheckpointPopupId((current) => (current === cp.id ? null : cp.id))
                  }
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: '50%',
                    background: '#d32f2f',
                    border: '2px solid #ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                    cursor: 'pointer',
                  }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="#ffffff"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z" />
                  </svg>
                </div>
              </div>
            </Marker>
          ))}

        {plannerStartMarker && routeLatLngs.length > 0 && (
        <Marker
          longitude={plannerStartMarker.coords.lng}
          latitude={plannerStartMarker.coords.lat}
          anchor="bottom"
          style={{ zIndex: showRouteStartPopup ? Z.ROUTE_ENDPOINT_POPUP : Z.START_ROUTE }}
        >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                transform: 'translateY(-4px)',
                position: 'relative',
                zIndex: 1,
              }}
            >
              {plannerStartMarker.label && showRouteStartPopup && (
                <RouteEndpointPopup
                  title="Route start"
                  label={plannerStartMarker.label}
                  accentColor="#1565C0"
                  onClose={() => setShowRouteStartPopup(false)}
                />
              )}
              {/* Start marker uses same pin shape as destination, but blue */}
              <div
                role="button"
                aria-label="Toggle route start details"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRouteStartPopup((v) => !v);
                }}
                style={{ cursor: 'pointer', lineHeight: 0 }}
              >
                <svg width="32" height="44" viewBox="0 0 24 34" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 0C5.924 0 1 4.924 1 11c0 6.5 7.5 14 10.2 22.2.2.6 1.4.6 1.6 0C15.5 25 23 17.5 23 11 23 4.924 18.076 0 12 0z"
                    fill="#1565C0"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                  />
                  <circle cx="12" cy="11" r="5" fill="#FFFFFF" />
                </svg>
              </div>
            </div>
          </Marker>
        )}

        {altRoutesGeoJson && (
          <Source id="alt-routes" type="geojson" data={altRoutesGeoJson}>
            <Layer
              id="alt-routes-layer"
              type="line"
              paint={{
                'line-color': [
                  'case',
                  ['==', ['get', 'routeIndex'], selectedRouteIndex],
                  '#1A5FB4',
                  '#90CAF9',
                ],
                'line-width': [
                  'case',
                  ['==', ['get', 'routeIndex'], selectedRouteIndex],
                  5,
                  3,
                ],
                'line-opacity': [
                  'case',
                  ['==', ['get', 'routeIndex'], selectedRouteIndex],
                  0.95,
                  0.6,
                ],
              }}
            />
          </Source>
        )}

        {!altRoutesGeoJson && primaryRouteGeoJson && (
          <Source id="primary-route" type="geojson" data={primaryRouteGeoJson}>
            <Layer
              id="primary-route-layer"
              type="line"
              paint={{ 'line-color': '#1A5FB4', 'line-width': 5, 'line-opacity': 0.95 }}
            />
          </Source>
        )}
      </MapLibreMap>

      {/* Simple Controls - Right side - Position adjusts based on panel collapse state */}
      <Box sx={{ position: 'absolute', bottom: controlsBottom, right: 20, zIndex: 1300, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, pointerEvents: 'auto', transition: 'bottom 0.3s ease-in-out' }}>
        {(() => {
          const gmapsUrl = googleMapsUrlForCurrentRoute();
          return (
            <>
              {gmapsUrl && (
                <Tooltip title="Navigate Route" placement="left" arrow>
                  <Fab
                    size="small"
                    component="a"
                    href={gmapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open in Google Maps"
                    sx={{ bgcolor: 'white', color: '#1A5FB4', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { bgcolor: '#f5f5f5' } }}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </Fab>
                </Tooltip>
              )}

              <Tooltip title={routeLatLngs.length > 0 ? 'Cancel' : 'Plan Route'} placement="left" arrow>
                <Fab
                  size="small"
                  onClick={() => {
                    if (routeLatLngs.length > 0) {
                      clearRoute();
                    } else {
                      openPlanRoute();
                    }
                  }}
                  aria-label={routeLatLngs.length > 0 ? 'Clear route' : 'Plan route'}
                  sx={{ bgcolor: 'white', color: '#1A5FB4', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { bgcolor: '#f5f5f5' } }}
                >
                  {routeLatLngs.length > 0 ? <CloseIcon fontSize="small" /> : <DirectionsIcon fontSize="small" />}
                </Fab>
              </Tooltip>

              <Tooltip title="Zoom In" placement="left" arrow>
                <Fab
                  size="small"
                  onClick={() => {
                    if (mapRef.current) {
                      const currentZoom = mapRef.current.getZoom();
                      mapRef.current.setZoom(currentZoom + 1);
                    }
                  }}
                  aria-label="Zoom in"
                  sx={{ bgcolor: 'white', color: '#1A5FB4', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { bgcolor: '#f5f5f5' } }}
                >
                  +
                </Fab>
              </Tooltip>

              <Tooltip title="Zoom Out" placement="left" arrow>
                <Fab
                  size="small"
                  onClick={() => {
                    if (mapRef.current) {
                      const currentZoom = mapRef.current.getZoom();
                      mapRef.current.setZoom(currentZoom - 1);
                    }
                  }}
                  aria-label="Zoom out"
                  sx={{ bgcolor: 'white', color: '#1A5FB4', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { bgcolor: '#f5f5f5' } }}
                >
                  −
                </Fab>
              </Tooltip>

              <Tooltip title="Locate Me" placement="left" arrow>
                <Fab
                  color="primary"
                  size="medium"
                  onClick={handleLocate}
                  aria-label="Locate me"
                  sx={{ bgcolor: '#1A5FB4', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', '&:hover': { bgcolor: '#0d47a1' } }}
                >
                  <MyLocationIcon />
                </Fab>
              </Tooltip>
            </>
          );
        })()}
      </Box>

      {/* Route selection - Optimized compact design - positioned above "Suggested nearby" panel */}
      {routeLoading && (
        <Box sx={{ 
          position: 'absolute', 
          bottom: routeSelectionBottom,
          left: { xs: 8, sm: 16 }, 
          right: { xs: 8, sm: 16 }, 
          zIndex: 1300, 
          pointerEvents: 'none',
          maxWidth: { md: '900px' },
          mx: { md: 'auto' },
          mb: 1
        }}>
          <Paper sx={{ 
            p: { xs: '8px 12px', sm: '10px 16px' }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            backgroundColor: '#f5f5f5',
            color: '#1A5FB4',
            fontWeight: 600,
            fontSize: { xs: '12px', sm: '13px' }
          }}>
            Calculating route...
          </Paper>
        </Box>
      )}
      {altRoutes.length > 0 && (
        <Box sx={{ 
          position: 'absolute', 
          bottom: routeSelectionBottom,
          left: { xs: 8, sm: 16 }, 
          right: { xs: 8, sm: 16 }, 
          zIndex: 1300, 
          pointerEvents: 'auto',
          maxWidth: { md: '900px' },
          mx: { md: 'auto' },
          mb: 1
        }}>
          <Paper sx={{ 
            p: { xs: '8px 12px', sm: '10px 16px' }, 
            display: 'flex', 
            alignItems: 'center', 
            gap: { xs: 0.5, sm: 1 }, 
            overflowX: 'auto',
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
          }}>
            <Box sx={{ 
              fontWeight: 600, 
              fontSize: { xs: '12px', sm: '13px' },
              color: '#666',
              mr: { xs: 0.5, sm: 1 },
              whiteSpace: 'nowrap',
              flexShrink: 0
            }}>
              {selectedRouteIndex === -1 ? 'Routes:' : 'Route:'}
            </Box>
            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 0.75 }, flex: 1, overflowX: 'auto', minWidth: 0 }}>
              {altRoutes.map((r, idx) => {
                const hours = Math.floor(r.durationMin / 60);
                const minutes = Math.round(r.durationMin % 60);
                const durationText = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                return (
                  <button
                    key={`rbtn-${idx}`}
                    onClick={() => { 
                      setSelectedRouteIndex(idx); 
                      setRouteLatLngs(r.latlngs);
                      setRouteSummary({ distanceMi: r.distanceMi, durationMin: r.durationMin });
                      // Fit bounds to selected route
                      fitBoundsToRoute(r.latlngs);
                    }}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: idx === selectedRouteIndex ? '2px solid #1A5FB4' : '1px solid #ddd',
                      cursor: 'pointer',
                      background: idx === selectedRouteIndex ? '#1A5FB4' : '#fff',
                      color: idx === selectedRouteIndex ? 'white' : '#333',
                      whiteSpace: 'nowrap',
                      fontSize: '12px',
                      fontWeight: idx === selectedRouteIndex ? 600 : 500,
                      transition: 'all 0.2s ease',
                      flexShrink: 0,
                      boxShadow: idx === selectedRouteIndex ? '0 2px 4px rgba(0,0,0,0.2)' : 'none'
                    }}
                    onMouseEnter={(e) => {
                      if (idx !== selectedRouteIndex) {
                        e.currentTarget.style.background = '#f5f5f5';
                        e.currentTarget.style.borderColor = '#1A5FB4';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (idx !== selectedRouteIndex) {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.borderColor = '#ddd';
                      }
                    }}
                  >
                    <span style={{ display: 'block', lineHeight: 1.2 }}>
                      <span style={{ fontWeight: 600 }}>{r.distanceMi.toFixed(1)} mi</span>
                    </span>
                    <span style={{ display: 'block', fontSize: '11px', opacity: 0.9, lineHeight: 1.2 }}>
                      {durationText}
                    </span>
                  </button>
                );
              })}
            </Box>
          </Paper>
        </Box>
      )}

      {/* Suggested nearby bottom panel - always show list for current map view.
          The `ref` lets us measure the panel's REAL rendered height (via
          ResizeObserver above) so the floating FAB stack and route picker
          pin themselves just above the panel regardless of whether it's
          collapsed, expanded-empty, or expanded-with-content. */}
      <Box
        ref={suggestedPanelRef}
        sx={{ position: 'absolute', left: { xs: 8, sm: 16 }, right: { xs: 8, sm: 16 }, bottom: SUGGESTED_PANEL_OFFSET, zIndex: 1300, pointerEvents: 'auto' }}
      >
        <Paper sx={{ width: '100%', bgcolor: 'white' }} elevation={3}>
          <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, py: 1, gap: 1 }}>
            <span style={{ fontWeight: 600 }}>In Route</span>
            <span style={{ color: '#666', fontSize: 12 }}>({suggestedStations.length})</span>
            <button
              onClick={() => setSuggestedCollapsed(v => !v)}
              style={{ marginLeft: 'auto', padding: '4px 8px', border: 'none', background: '#eee', borderRadius: 4, cursor: 'pointer', fontSize: '13px' }}
            >
              {suggestedCollapsed ? 'Expand' : 'Collapse'}
            </button>
          </Box>
          {!suggestedCollapsed && (
            <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
              <List dense sx={{ pt: 0 }}>
                {suggestedStations.length === 0 && (
                  <ListItem sx={{ py: 1.5 }}>
                    <ListItemText 
                      primary={
                        stations.length === 0
                          ? 'Loading stations for this view...'
                          : 'No stations found in this radius.'
                      }
                      primaryTypographyProps={{ 
                        style: { color: '#666', fontSize: '14px', textAlign: 'center' } 
                      }}
                    />
                  </ListItem>
                )}
                {suggestedStations.map(({ s, d, p }) => (
                  <ListItem key={`sugg-${s.stationCode}`} disablePadding>
                    <ListItemButton onClick={() => {
                      const coords = { lat: s.latitude, lng: s.longitude };
                      setCenter(coords);
                      setCenterForSuggestions(coords); // Update suggested stations center
                      centerForSuggestionsRef.current = coords;
                      if (mapRef.current) {
                        const map = mapRef.current;
                        const currentZoom = map.getZoom();
                        const targetZoom = Math.max(typeof currentZoom === 'number' ? currentZoom : 12, 12);
                        map.flyTo({ center: [coords.lng, coords.lat], zoom: targetZoom, essential: true });
                      }
                      // Don't auto-start directions - user can click on marker popup to get directions
                    }}>
                      <ListItemText
                        primary={s.stationName}
                      secondary={`${s.city}, ${s.state} • ${d.toFixed(1)} mi${Number.isFinite(p) ? ` • $${p.toFixed(3)}` : ''}`}
                      />
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginLeft: 'auto' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            startDirectionsTo({ lat: s.latitude, lng: s.longitude });
                          }}
                          disabled={routeLoading}
                          style={{ padding: '6px 10px', background: '#1A5FB4', color: 'white', border: 'none', borderRadius: 4, cursor: routeLoading ? 'not-allowed' : 'pointer', opacity: routeLoading ? 0.7 : 1 }}
                        >
                          {routeLoading ? 'Calculating...' : 'Get Directions'}
                        </button>
                        {routeLatLngs.length > 0 && (
                          <a
                            onClick={(e) => e.stopPropagation()}
                            href={googleMapsDirectionsUrl({ lat: s.latitude, lng: s.longitude })}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ padding: '6px 10px', background: '#0F9D58', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', textDecoration: 'none' }}
                          >
                            Open in Google Maps
                          </a>
                        )}
                      </div>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
          {/* Disclaimer */}
          <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
            <Typography variant="caption" sx={{ fontSize: '13px', color: '#000', fontWeight: 'bold', lineHeight: 1.4, display: 'block' }}>
              Map shows stations with discounts. Prices set by station and may change without notice.
            </Typography>
          </Box>
        </Paper>
      </Box>

      <RouteCalculationOverlay
        open={routeLoading || pendingRouteStations || (loadingStations && stationsLoadReason === 'route')}
        variant={
          routeLoading
            ? (isPlannerSubmitting ? 'planner' : 'directions')
            : 'stations'
        }
      />
    </Box>
  );
};

export default MapRoutesMapLibre;


